<template>
      <div class="questionario-content">
        <transition-group name="toast" tag="div" class="toast-stack">
          <div
            v-for="toast in toasts"
            :key="toast.id"
            class="toast"
            :class="`toast--${toast.type}`"
          >
            <span class="toast-message">{{ toast.message }}</span>
            <button type="button" class="toast-close" @click="dismissToast(toast.id)">×</button>
          </div>
        </transition-group>
        <div class="card questionario-card" ref="printableCard">
          <header class="card-header">
            <div class="card-header__title">
              <h1>Questionario Iniziale</h1>
            </div>
            <div class="card-header__right">
              <div class="meta">
                <span v-if="questionarioInfo.updatedAt" class="muted">
                  Ultimo aggiornamento: {{ formatDate(questionarioInfo.updatedAt) }}
                </span>
                <span v-else class="muted">Nessun dato salvato</span>
              </div>
              <div class="card-header__actions">
                <button
                  type="button"
                  class="ghost-button"
                  @click="copyQuestionarioAnswers"
                  :disabled="copyingAnswers"
                  :aria-busy="copyingAnswers"
                  title="Copia questionario"
                  aria-label="Copia questionario"
                >
                  <span v-if="copyingAnswers" class="ghost-button__spinner" aria-hidden="true"></span>
                  <span v-else class="material-icons" aria-hidden="true" style="color:#fff">file_copy</span>
                </button>
                <button
                  type="button"
                  class="ghost-button"
                  @click="pasteQuestionarioAnswers"
                  :disabled="pastingAnswers"
                  :aria-busy="pastingAnswers"
                  title="Incolla questionario"
                  aria-label="Incolla questionario"
                >
                  <span v-if="pastingAnswers" class="ghost-button__spinner" aria-hidden="true"></span>
                  <span v-else class="material-icons" aria-hidden="true" style="color:#fff">content_paste</span>
                </button>
                <button
                  type="button"
                  class="ghost-button"
                  @click="exportFormAsPdf"
                  :disabled="pdfExporting"
                  title="Esporta PDF"
                  aria-label="Esporta PDF"
                  :aria-busy="pdfExporting"
                >
                  <span v-if="pdfExporting" class="ghost-button__spinner" aria-hidden="true"></span>
                  <span v-else class="material-icons" aria-hidden="true" style="color:#fff">picture_as_pdf</span>
                </button>
                <button
                  type="button"
                  class="ghost-button"
                  @click="copyFormLink"
                  title="Copia link form"
                  aria-label="Copia link form"
                >
                  <span class="material-icons" aria-hidden="true" style="color:#fff">content_copy</span>
                </button>
                <button
                  type="button"
                  class="ghost-button"
                  @click="togglePartialQuestionario"
                  :disabled="partialSaving"
                  :title="questionarioPartial ? 'Rendi completo' : 'Rendi parziale'"
                  :aria-label="questionarioPartial ? 'Rendi completo' : 'Rendi parziale'"
                >
                  <span class="material-icons" aria-hidden="true" style="color:#fff">
                    {{ partialSaving ? 'hourglass_top' : questionarioPartial ? 'rule' : 'rule_folder' }}
                  </span>
                </button>
              </div>
            </div>
          </header>
          <p
            v-if="questionarioPartial"
            class="muted"
            style="margin: 0 0 12px;"
          >
            Modalità parziale attiva: foto e misure non sono obbligatorie per l’utente.
          </p>
          <div v-if="canManageAdmin" class="admin-wrapper">
            <div class="admin-controls">
              <div class="admin-controls__status">
                <span class="admin-chip" :class="adminChipClass">
                  {{ adminStatusLabel }}
                </span>
                <span v-if="adminState.updatedAt" class="admin-status-note">
                  Aggiornato: {{ formatDate(adminState.updatedAt) }}
                </span>
                <span v-if="adminLoading" class="admin-status-note">Aggiornamento…</span>
              </div>
              <div class="admin-controls__actions">
                <button
                  type="button"
                  class="admin-btn admin-btn--green"
                  @click="handleAdminApprove"
                  :disabled="adminSaving || adminLoading"
                >
                  Approva
                </button>
                <button
                  type="button"
                  class="admin-btn admin-btn--orange"
                  @click="toggleNeedsUpdate"
                  :disabled="adminSaving"
                >
                  Richiedi
                </button>
              </div>
            </div>
            <div v-if="needsUpdateOpen" class="admin-needs-box">
              <input
                v-model.trim="needsUpdateNote"
                class="admin-note-input"
                type="text"
                placeholder="Scrivi la nota per l'utente…"
              />
              <div class="admin-controls__actions">
                <button
                  type="button"
                  class="admin-btn admin-btn--green staff-button-positive"
                  @click="saveNeedsUpdateRequest"
                  :disabled="adminSaving"
                >
                  Salva
                </button>
                <button type="button" class="admin-btn admin-btn--grey" @click="toggleNeedsUpdate">
                  Chiudi
                </button>
              </div>
            </div>
            <p v-else-if="adminState.note" class="admin-note">Nota: “{{ adminState.note }}”</p>
          </div>
          <p v-else class="admin-helper muted">
            Collega questo questionario a un programma per gestire lo stato admin.
          </p>
          <p v-if="adminError" class="admin-error">{{ adminError }}</p>
          <div v-if="loading" class="state state-loading">Caricamento questionario…</div>
          <div v-else-if="errorMessage" class="state state-error">{{ errorMessage }}</div>
          <div v-else>
            <form class="qi-form" @submit.prevent="saveQuestionario">
              <!-- Informazioni generali -->
              <section class="qi-block">
                <header class="qi-block__header">
                  <h2 class="qi-block__title">Informazioni generali</h2>
                </header>
                <div class="qi-block__body">
                  <div class="grid two birthdate-grid">
                    <label class="birthdate-field birthdate-inline">
                      <span>Data di nascita</span>
                      <div class="birthdate-controls birthdate-controls--inline">
                        <select v-model="birthdateParts.day">
                          <option value="">GG</option>
                          <option v-for="opt in DAY_OPTIONS" :key="opt.value" :value="opt.value">
                            {{ opt.label }}
                          </option>
                        </select>
                        <select v-model="birthdateParts.month">
                          <option value="">MM</option>
                          <option v-for="opt in MONTH_OPTIONS" :key="opt.value" :value="opt.value">
                            {{ opt.label }}
                          </option>
                        </select>
                        <select v-model="birthdateParts.year">
                          <option value="">AAAA</option>
                          <option v-for="opt in YEAR_OPTIONS" :key="opt" :value="opt">
                            {{ opt }}
                          </option>
                        </select>
                      </div>
                    </label>
                    <label>
                      <span>Professione</span>
                      <input v-model.trim="form.professione" type="text" placeholder="Es. impiegata, studentessa…" />
                    </label>
                    <label>
                      <span>Turnista</span>
                      <select v-model="form.turnista">
                        <option value="">Seleziona…</option>
                        <option v-for="opt in TURNISTA_OPTIONS" :key="opt" :value="opt">{{ opt }}</option>
                      </select>
                    </label>
                    <label>
                      <span>Ore lavorative / giorno</span>
                      <select v-model="form.oreLavorative">
                        <option value="">Seleziona…</option>
                        <option v-for="opt in WORK_HOURS_OPTIONS" :key="opt" :value="opt">{{ opt }}</option>
                      </select>
                    </label>
                    <label>
                      <span>Stile di vita</span>
                      <select v-model="form.stileVita">
                        <option value="">Seleziona…</option>
                        <option v-for="opt in LIFESTYLE_OPTIONS" :key="opt" :value="opt">{{ opt }}</option>
                      </select>
                      <small class="muted">Una persona dinamica mantiene uno stile attivo anche fuori dall'allenamento.</small>
                    </label>
                    <label>
                      <span>Ore sonno / notte</span>
                      <select v-model="form.oreSonno">
                        <option value="">Seleziona…</option>
                        <option v-for="opt in SLEEP_HOURS_OPTIONS" :key="opt" :value="opt">{{ opt }}</option>
                      </select>
                    </label>
                    <label>
                      <span>Qualità del sonno</span>
                      <select v-model="form.qualitaSonno">
                        <option value="">Seleziona…</option>
                        <option v-for="opt in SLEEP_QUALITY_OPTIONS" :key="opt" :value="opt">{{ opt }}</option>
                      </select>
                    </label>
                    <label>
                      <span>Periodi di forte stress</span>
                      <select v-model="form.periodoStressante">
                        <option value="">Seleziona…</option>
                        <option v-for="opt in YES_NO_OPTIONS" :key="opt" :value="opt">{{ opt }}</option>
                      </select>
                    </label>
                  </div>
                </div>
              </section>

              <!-- Anamnesi sportiva -->
              <section class="qi-block">
                <header class="qi-block__header">
                  <h2 class="qi-block__title">Anamnesi sportiva</h2>
                </header>
                <div class="qi-block__body">
                  <div class="grid two">
                    <label>
                      <span>Attualmente ti alleni?</span>
                      <select v-model="form.allenamentoAttuale">
                        <option value="">Seleziona…</option>
                        <option v-for="opt in ALLENAMENTO_ATTUALE_OPTIONS" :key="opt" :value="opt">{{ opt }}</option>
                      </select>
                    </label>
                    <label>
                      <span>Esperienza in sala pesi</span>
                      <select v-model="form.esperienzaSala">
                        <option value="">Seleziona…</option>
                        <option v-for="opt in ESPERIENZA_SALA_OPTIONS" :key="opt" :value="opt">{{ opt }}</option>
                      </select>
                    </label>
                    <label>
                      <span>Ti sei allenata con un personal trainer?</span>
                      <select v-model="form.ptPresenza">
                        <option value="">Seleziona…</option>
                        <option v-for="opt in PT_OPTIONS" :key="opt" :value="opt">{{ opt }}</option>
                      </select>
                    </label>
                    <label>
                      <span>Ti sei mai allenata in videochiamata con un personal trainer?</span>
                      <select v-model="form.ptVideocall">
                        <option value="">Seleziona…</option>
                        <option v-for="opt in PT_OPTIONS" :key="opt" :value="opt">{{ opt }}</option>
                      </select>
                    </label>
                  </div>

                  <div v-if="form.allenamentoAttuale === 'Si'" class="checkbox-group">
                    <span class="group-label">Tipologia di allenamento</span>
                    <label v-for="opt in TIPI_ALLENAMENTO" :key="opt" class="checkbox-item">
                      <input type="checkbox" :value="opt" v-model="form.tipiAllenamento" />
                      <span>{{ opt }}</span>
                    </label>
                  </div>
                </div>
              </section>

              <!-- Anamnesi fisiologica -->
              <section class="qi-block">
                <header class="qi-block__header">
                  <h2 class="qi-block__title">Anamnesi fisiologica</h2>
                </header>
                <div class="qi-block__body">
                  <div class="grid two">
                    <label>
                      <span>Alvo (regolarità intestinale)</span>
                      <select v-model="form.alvo">
                        <option value="">Seleziona…</option>
                        <option v-for="opt in ALVO_OPTIONS" :key="opt" :value="opt">{{ opt }}</option>
                      </select>
                    </label>
                    <label>
                      <span>Ciclo mestruale</span>
                      <select v-model="form.cicloMestruale">
                        <option value="">Seleziona…</option>
                        <option v-for="opt in CICLO_MESTRUALE_OPTIONS" :key="opt" :value="opt">{{ opt }}</option>
                      </select>
                    </label>
                    <label>
                      <span>Litri di acqua al giorno</span>
                      <select v-model="form.litriAcqua">
                        <option value="">Seleziona…</option>
                        <option v-for="opt in ACQUA_OPTIONS" :key="opt" :value="opt">{{ opt }}</option>
                      </select>
                    </label>
                    <label>
                      <span>Fumo</span>
                      <select v-model="form.fumo">
                        <option value="">Seleziona…</option>
                        <option v-for="opt in YES_NO_OPTIONS" :key="opt" :value="opt">{{ opt }}</option>
                      </select>
                    </label>
                    <label>
                      <span>Bevande alcoliche</span>
                      <select v-model="form.bevandeAlcoliche">
                        <option value="">Seleziona…</option>
                        <option v-for="opt in ALCOL_OPTIONS" :key="opt" :value="opt">{{ opt }}</option>
                      </select>
                    </label>
                  </div>

                  <div class="repeater">
                    <header class="qi-subheader">
                      <h3 class="qi-subheader__title">Gravidanze</h3>
                      <button type="button" class="link staff-button-positive" @click="addGravidanza">+ Aggiungi gravidanza</button>
                    </header>
                    <div
                      v-for="(item, index) in form.gravidanze"
                      :key="`gravidanza_${index}`"
                      class="repeater-item"
                    >
                      <label>
                        <span>Anno</span>
                        <input v-model="item.anno" type="text" placeholder="Es. 2020" />
                      </label>
                      <label>
                        <span>Tipo di parto</span>
                        <input v-model.trim="item.parto" type="text" placeholder="Es. naturale, cesareo…" />
                      </label>
                      <button type="button" class="link danger staff-button-danger" @click="removeGravidanza(index)">Rimuovi</button>
                    </div>
                  </div>
                </div>
              </section>

              <!-- Anamnesi patologica -->
              <section class="qi-block">
                <header class="qi-block__header">
                  <div class="qi-block__header-text">
                    <h2 class="qi-block__title">Anamnesi patologica</h2>
                    <p class="qi-block__subtitle muted">
                      Segnala eventuali patologie. Seleziona "Diagnosi certificata" solo se possiedi una certificazione e utilizza il campo dedicato per riportare i dettagli.
                    </p>
                  </div>
                </header>
                <div class="qi-block__body">
                  <div class="accordion-question">
                    <span>Hai patologie metaboliche?</span>
                    <div class="option-toggle">
                      <label
                        :class="['choice-radio', { 'choice-radio--checked': form.haPatologieMetaboliche === 'Si' }]"
                      >
                        <input type="radio" value="Si" v-model="form.haPatologieMetaboliche" />
                        <span>Si</span>
                      </label>
                      <label
                        :class="['choice-radio', { 'choice-radio--checked': form.haPatologieMetaboliche === 'No' }]"
                      >
                        <input type="radio" value="No" v-model="form.haPatologieMetaboliche" />
                        <span>No</span>
                      </label>
                    </div>
                  </div>

                  <transition name="fold">
                    <div v-if="form.haPatologieMetaboliche === 'Si'" class="accordion-body">
                      <div class="pathology-grid">
                        <div v-for="item in PATHOLOGIES" :key="item.key" class="pathology-card">
                          <label class="checkbox-item">
                            <input type="checkbox" v-model="form.patologie[item.key].presente" />
                            <span>{{ item.label }}</span>
                          </label>
                          <div v-if="form.patologie[item.key].presente" class="pathology-body">
                            <label>
                              <span>Diagnosi</span>
                              <select v-model="form.patologie[item.key].diagnosi">
                                <option value="">Seleziona…</option>
                                <option v-for="opt in DIAGNOSIS_OPTIONS" :key="opt" :value="opt">{{ opt }}</option>
                              </select>
                            </label>
                        <div
                          v-if="form.patologie[item.key].diagnosi === 'Diagnosi certificata'"
                          class="certification-panel"
                        >
                              <div class="certification-upload">
                                <span>Certificazione (file)</span>
                                <input
                                  type="file"
                                  accept=".pdf,image/*"
                                  @change="onCertificationFileChange('patologie', item.key, $event)"
                                />
                                <div
                                  v-if="isCertificationUploading('patologie', item.key)"
                                  class="upload-progress"
                                >
                                  <span class="spinner"></span>
                                  <span>Caricamento…</span>
                                </div>
                                <div
                                  v-else-if="form.patologie[item.key].certificazioneFile"
                                  class="file-meta certification-file-meta"
                                >
                                  <div
                                    v-if="isImageFile(form.patologie[item.key].certificazioneFile)"
                                    class="file-preview"
                                  >
                                    <img
                                      :src="form.patologie[item.key].certificazioneFile.url"
                                      alt="Anteprima certificazione"
                                      loading="lazy"
                                    />
                                  </div>
                                  <div
                                    v-else-if="isPdfFile(form.patologie[item.key].certificazioneFile)"
                                    class="file-preview file-preview--pdf"
                                  >
                                    <iframe
                                      :src="form.patologie[item.key].certificazioneFile.url"
                                      title="Anteprima certificazione PDF"
                                      loading="lazy"
                                    ></iframe>
                                  </div>
                                  <a
                                    :href="form.patologie[item.key].certificazioneFile.url"
                                    target="_blank"
                                    rel="noopener"
                                  >
                                    {{ form.patologie[item.key].certificazioneFile.name }}
                                  </a>
                                  <button
                                    type="button"
                                    class="link danger staff-button-danger"
                                    @click="removeCertificationFile('patologie', item.key)"
                                  >
                                    Rimuovi file
                                  </button>
                                </div>
                              </div>
                            </div>
                            <label>
                              <span>Note aggiuntive</span>
                              <textarea
                                v-model.trim="form.patologie[item.key].note"
                                rows="3"
                                placeholder="Informazioni utili allo staff"
                              ></textarea>
                            </label>
                          </div>
                        </div>
                      </div>

                      <div class="repeater">
                        <header class="qi-subheader">
                          <h3 class="qi-subheader__title">Altre patologie</h3>
                          <button type="button" class="link staff-button-positive" @click="addAltraPatologia">+ Aggiungi</button>
                        </header>
                        <div
                          v-for="(item, index) in form.patologieAltre"
                          :key="`patologia_extra_${index}`"
                          class="repeater-item"
                        >
                          <label>
                            <span>Patologia</span>
                            <input v-model.trim="item.nome" type="text" placeholder="Nome della patologia" />
                          </label>
                          <label>
                            <span>Diagnosi</span>
                            <select
                              v-model="item.diagnosi"
                              @change="handleDiagnosiChange('patologieAltre', index, $event.target.value)"
                            >
                              <option value="">Seleziona…</option>
                              <option v-for="opt in DIAGNOSIS_OPTIONS" :key="opt" :value="opt">{{ opt }}</option>
                            </select>
                          </label>
                          <div v-if="item.diagnosi === 'Diagnosi certificata'" class="certification-panel">
                            <div class="certification-upload">
                              <span>Certificazione (file)</span>
                              <input
                                type="file"
                                accept=".pdf,image/*"
                                @change="onCertificationFileChange('patologieAltre', index, $event)"
                              />
                              <div v-if="isCertificationUploading('patologieAltre', index)" class="upload-progress">
                                <span class="spinner"></span>
                                <span>Caricamento…</span>
                              </div>
                              <div v-else-if="item.certificazioneFile" class="file-meta certification-file-meta">
                                <div v-if="isImageFile(item.certificazioneFile)" class="file-preview">
                                  <img
                                    :src="item.certificazioneFile.url"
                                    alt="Anteprima certificazione"
                                    loading="lazy"
                                  />
                                </div>
                                <div
                                  v-else-if="isPdfFile(item.certificazioneFile)"
                                  class="file-preview file-preview--pdf"
                                >
                                  <iframe
                                    :src="item.certificazioneFile.url"
                                    title="Anteprima certificazione PDF"
                                    loading="lazy"
                                  ></iframe>
                                </div>
                                <a :href="item.certificazioneFile.url" target="_blank" rel="noopener">
                                  {{ item.certificazioneFile.name }}
                                </a>
                                <button
                                  type="button"
                                  class="link danger staff-button-danger"
                                  @click="removeCertificationFile('patologieAltre', index)"
                                >
                                  Rimuovi file
                                </button>
                              </div>
                            </div>
                          </div>
                          <label>
                            <span>Note</span>
                            <textarea v-model.trim="item.note" rows="3"></textarea>
                          </label>
                          <button type="button" class="link danger staff-button-danger" @click="removeAltraPatologia(index)">Rimuovi</button>
                        </div>
                      </div>

                      <label class="checkbox-item declaration">
                        <input type="checkbox" v-model="form.dichiarazionePatologie" />
                        <span>
                          Dichiaro di aver indicato tutte le patologie conosciute e sollevo la società da responsabilità in caso di dichiarazioni false o mendaci.
                        </span>
                      </label>
                    </div>
                  </transition>
                </div>
              </section>

              <!-- Patologie muscolo-scheletriche -->
              <section class="qi-block">
                <header class="qi-block__header">
                  <h2 class="qi-block__title">Patologie muscolo-scheletriche e storico</h2>
                </header>
                <div class="qi-block__body">
                  <div class="accordion-question">
                    <span>Hai patologie muscolo-scheletriche?</span>
                    <div class="option-toggle">
                      <label :class="['choice-radio', { 'choice-radio--checked': form.haPatologieMuscolo === 'Si' }]">
                        <input type="radio" value="Si" v-model="form.haPatologieMuscolo" />
                        <span>Si</span>
                      </label>
                      <label :class="['choice-radio', { 'choice-radio--checked': form.haPatologieMuscolo === 'No' }]">
                        <input type="radio" value="No" v-model="form.haPatologieMuscolo" />
                        <span>No</span>
                      </label>
                    </div>
                  </div>

                  <transition name="fold">
                    <div v-if="form.haPatologieMuscolo === 'Si'" class="accordion-body">
                      <div class="pathology-grid">
                        <div v-for="item in MUSCLE_PATHOLOGIES" :key="item.key" class="pathology-card">
                          <label class="checkbox-item">
                            <input type="checkbox" v-model="form.patologieMuscolo[item.key].presente" />
                            <span>{{ item.label }}</span>
                          </label>
                          <div v-if="form.patologieMuscolo[item.key].presente" class="pathology-body">
                            <label>
                              <span>Diagnosi</span>
                              <select v-model="form.patologieMuscolo[item.key].diagnosi">
                                <option value="">Seleziona…</option>
                                <option v-for="opt in DIAGNOSIS_OPTIONS" :key="opt" :value="opt">{{ opt }}</option>
                              </select>
                            </label>
                            <label v-if="item.askCm">
                              <span>Centimetri coinvolti</span>
                              <input v-model.trim="form.patologieMuscolo[item.key].cm" type="text" placeholder="Es. 2 cm" />
                            </label>
                            <div
                              v-if="form.patologieMuscolo[item.key].diagnosi === 'Diagnosi certificata'"
                              class="certification-panel"
                            >
                              <div
                                v-if="form.patologieMuscolo[item.key].diagnosi === 'Diagnosi certificata'"
                                class="certification-panel"
                              >
                                <div class="certification-upload">
                                  <span>Certificazione (file)</span>
                                  <input
                                    type="file"
                                    accept=".pdf,image/*"
                                    @change="onCertificationFileChange('patologieMuscolo', item.key, $event)"
                                  />
                                  <div
                                    v-if="isCertificationUploading('patologieMuscolo', item.key)"
                                    class="upload-progress"
                                  >
                                    <span class="spinner"></span>
                                    <span>Caricamento…</span>
                                  </div>
                                  <div
                                    v-else-if="form.patologieMuscolo[item.key].certificazioneFile"
                                    class="file-meta certification-file-meta"
                                  >
                                    <div
                                      v-if="isImageFile(form.patologieMuscolo[item.key].certificazioneFile)"
                                      class="file-preview"
                                    >
                                      <img
                                        :src="form.patologieMuscolo[item.key].certificazioneFile.url"
                                        alt="Anteprima certificazione"
                                        loading="lazy"
                                      />
                                    </div>
                                    <div
                                      v-else-if="isPdfFile(form.patologieMuscolo[item.key].certificazioneFile)"
                                      class="file-preview file-preview--pdf"
                                    >
                                      <iframe
                                        :src="form.patologieMuscolo[item.key].certificazioneFile.url"
                                        title="Anteprima certificazione PDF"
                                        loading="lazy"
                                      ></iframe>
                                    </div>
                                    <a
                                      :href="form.patologieMuscolo[item.key].certificazioneFile.url"
                                      target="_blank"
                                      rel="noopener"
                                    >
                                      {{ form.patologieMuscolo[item.key].certificazioneFile.name }}
                                    </a>
                                    <button
                                      type="button"
                                      class="link danger staff-button-danger"
                                      @click="removeCertificationFile('patologieMuscolo', item.key)"
                                    >
                                      Rimuovi file
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <label>
                              <span>Note aggiuntive</span>
                              <textarea v-model.trim="form.patologieMuscolo[item.key].note" rows="3"></textarea>
                            </label>
                          </div>
                        </div>
                      </div>

                      <div class="repeater">
                        <header class="qi-subheader">
                          <h3 class="qi-subheader__title">Altre problematiche muscolo-scheletriche</h3>
                          <button type="button" class="link staff-button-positive" @click="addProblematicaMuscolo">+ Aggiungi</button>
                        </header>
                        <div
                          v-for="(item, index) in form.patologieMuscoloAltre"
                          :key="`problematica_${index}`"
                          class="repeater-item"
                        >
                          <label>
                            <span>Zona interessata</span>
                            <input v-model.trim="item.zona" type="text" placeholder="Es. polso destro" />
                          </label>
                          <label>
                            <span>Diagnosi</span>
                            <select
                              v-model="item.diagnosi"
                              @change="handleDiagnosiChange('patologieMuscoloAltre', index, $event.target.value)"
                            >
                              <option value="">Seleziona…</option>
                              <option v-for="opt in DIAGNOSIS_OPTIONS" :key="opt" :value="opt">{{ opt }}</option>
                            </select>
                          </label>
                          <div v-if="item.diagnosi === 'Diagnosi certificata'" class="certification-panel">
                            <div class="certification-upload">
                              <span>Certificazione (file)</span>
                              <input
                                type="file"
                                accept=".pdf,image/*"
                                @change="onCertificationFileChange('patologieMuscoloAltre', index, $event)"
                              />
                              <div
                                v-if="isCertificationUploading('patologieMuscoloAltre', index)"
                                class="upload-progress"
                              >
                                <span class="spinner"></span>
                                <span>Caricamento…</span>
                              </div>
                              <div v-else-if="item.certificazioneFile" class="file-meta certification-file-meta">
                                <div v-if="isImageFile(item.certificazioneFile)" class="file-preview">
                                  <img
                                    :src="item.certificazioneFile.url"
                                    alt="Anteprima certificazione"
                                    loading="lazy"
                                  />
                                </div>
                                <div
                                  v-else-if="isPdfFile(item.certificazioneFile)"
                                  class="file-preview file-preview--pdf"
                                >
                                  <iframe
                                    :src="item.certificazioneFile.url"
                                    title="Anteprima certificazione PDF"
                                    loading="lazy"
                                  ></iframe>
                                </div>
                                <a :href="item.certificazioneFile.url" target="_blank" rel="noopener">
                                  {{ item.certificazioneFile.name }}
                                </a>
                                <button
                                  type="button"
                                  class="link danger staff-button-danger"
                                  @click="removeCertificationFile('patologieMuscoloAltre', index)"
                                >
                                  Rimuovi file
                                </button>
                              </div>
                            </div>
                          </div>
                          <label>
                            <span>Note</span>
                            <textarea v-model.trim="item.note" rows="3"></textarea>
                          </label>
                          <button type="button" class="link danger staff-button-danger" @click="removeProblematicaMuscolo(index)">Rimuovi</button>
                        </div>
                      </div>

                      <div class="repeater">
                        <header class="qi-subheader">
                          <h3 class="qi-subheader__title">Infortuni</h3>
                          <button type="button" class="link staff-button-positive" @click="addInfortunio">+ Aggiungi</button>
                        </header>
                        <div v-for="(item, index) in form.infortuni" :key="`infortunio_${index}`" class="repeater-item">
                          <label>
                            <span>Descrizione</span>
                            <textarea v-model.trim="item.descrizione" rows="2"></textarea>
                          </label>
                          <label>
                            <span>Anno</span>
                            <input v-model.trim="item.anno" type="text" placeholder="Es. 2019" />
                          </label>
                          <label>
                            <span>Limitazioni attuali?</span>
                            <select v-model="item.limitazioni">
                              <option value="">Seleziona…</option>
                              <option v-for="opt in YES_NO_OPTIONS" :key="opt" :value="opt">{{ opt }}</option>
                            </select>
                          </label>
                          <button type="button" class="link danger staff-button-danger" @click="removeInfortunio(index)">Rimuovi</button>
                        </div>
                      </div>

                      <div class="repeater">
                        <header class="qi-subheader">
                          <h3 class="qi-subheader__title">Interventi</h3>
                          <button type="button" class="link staff-button-positive" @click="addIntervento">+ Aggiungi</button>
                        </header>
                        <div v-for="(item, index) in form.interventi" :key="`intervento_${index}`" class="repeater-item">
                          <label>
                            <span>Descrizione</span>
                            <textarea v-model.trim="item.descrizione" rows="2"></textarea>
                          </label>
                          <label>
                            <span>Anno</span>
                            <input v-model.trim="item.anno" type="text" placeholder="Es. 2021" />
                          </label>
                          <label>
                            <span>Limitazioni attuali?</span>
                            <select v-model="item.limitazioni">
                              <option value="">Seleziona…</option>
                              <option v-for="opt in YES_NO_OPTIONS" :key="opt" :value="opt">{{ opt }}</option>
                            </select>
                          </label>
                          <button type="button" class="link danger staff-button-danger" @click="removeIntervento(index)">Rimuovi</button>
                        </div>
                      </div>

                      <label>
                        <span>Note su esercizi o movimenti da evitare</span>
                        <textarea v-model.trim="form.noteLimitazioni" rows="3"></textarea>
                      </label>

                      <label>
                        <span>Farmaci assunti (specifica la patologia)</span>
                        <textarea v-model.trim="form.farmaciAssunti" rows="3"></textarea>
                      </label>
                    </div>
                  </transition>
                </div>
              </section>

              <!-- Diario alimentare -->
              <section class="qi-block">
                <header class="qi-block__header">
                  <h2 class="qi-block__title">Diario alimentare</h2>
                </header>
                <div class="qi-block__body">
                  <div class="grid two">
                    <label>
                      <span>Regime alimentare</span>
                      <select v-model="form.regimeAlimentare">
                        <option value="">Seleziona…</option>
                        <option v-for="opt in REGIME_ALIMENTARE_OPTIONS" :key="opt" :value="opt">{{ opt }}</option>
                      </select>
                    </label>
                    <label>
                      <span>Segui un regime alimentare controllato?</span>
                      <select v-model="form.regimeControllato">
                        <option value="">Seleziona…</option>
                        <option v-for="opt in YES_NO_OPTIONS" :key="opt" :value="opt">{{ opt }}</option>
                      </select>
                    </label>
                    <label>
                      <span>Quanti pasti fai al giorno?</span>
                      <select v-model="form.pastiGiorno">
                        <option value="">Seleziona…</option>
                        <option v-for="opt in PASTI_OPTIONS" :key="opt" :value="opt">{{ opt }}</option>
                      </select>
                    </label>
                    <label>
                      <span>Ti capita di saltare i pasti?</span>
                      <select v-model="form.saltarePasti">
                        <option value="">Seleziona…</option>
                        <option v-for="opt in SALTARE_PASTI_OPTIONS" :key="opt" :value="opt">{{ opt }}</option>
                      </select>
                    </label>
                  </div>

                  <label>
                    <span>Regimi controllati seguiti in passato (ed esito)</span>
                    <textarea v-model.trim="form.regimiControllatiPassato" rows="3"></textarea>
                  </label>

                  <p class="diario-hint">
                    Descrivi per ogni momento della giornata cosa mangi precisamente in una giornata tipo, inserisci le grammature se le conosci.
                  </p>

                  <div class="grid two">
                    <label class="diario-label">
                      <span>Colazione</span>
                      <textarea v-model.trim="form.diarioColazione" rows="3" class="diario-textarea"></textarea>
                    </label>
                    <label class="diario-label">
                      <span>Metà mattina</span>
                      <textarea v-model.trim="form.diarioMetaMattina" rows="3" class="diario-textarea"></textarea>
                    </label>
                    <label class="diario-label">
                      <span>Pranzo</span>
                      <textarea v-model.trim="form.diarioPranzo" rows="3" class="diario-textarea"></textarea>
                    </label>
                    <label class="diario-label">
                      <span>Metà pomeriggio</span>
                      <textarea v-model.trim="form.diarioMetaPomeriggio" rows="3" class="diario-textarea"></textarea>
                    </label>
                    <label class="diario-label">
                      <span>Cena</span>
                      <textarea v-model.trim="form.diarioCena" rows="3" class="diario-textarea"></textarea>
                    </label>
                  </div>

                  <div class="grid two">
                    <label>
                      <span>Hai mai usato integratori alimentari?</span>
                      <select v-model="form.integratoriUsati">
                        <option value="">Seleziona…</option>
                        <option v-for="opt in YES_NO_OPTIONS" :key="opt" :value="opt">{{ opt }}</option>
                      </select>
                    </label>
                    <label v-if="form.integratoriUsati === 'Si'">
                      <span>Quali integratori hai usato?</span>
                      <textarea v-model.trim="form.integratoriUsatiQuali" rows="2"></textarea>
                    </label>
                    <label>
                      <span>Integri attualmente la tua alimentazione?</span>
                      <select v-model="form.integratoriAttuali">
                        <option value="">Seleziona…</option>
                        <option v-for="opt in YES_NO_OPTIONS" :key="opt" :value="opt">{{ opt }}</option>
                      </select>
                    </label>
                    <label v-if="form.integratoriAttuali === 'Si'">
                      <span>Quali integratori assumi?</span>
                      <textarea v-model.trim="form.integratoriAttualiQuali" rows="2"></textarea>
                    </label>
                  </div>
                </div>
              </section>

              <!-- Allenamento -->
              <section class="qi-block">
                <header class="qi-block__header">
                  <h2 class="qi-block__title">Il nostro allenamento</h2>
                </header>
                <div class="qi-block__body">
                  <div class="grid two">
                    <label>
                      <span>Dove puoi allenarti con la nostra programmazione?</span>
                      <select v-model="form.allenamentoLuogo">
                        <option value="">Seleziona…</option>
                        <option v-for="opt in LUOGO_ALLENAMENTO_OPTIONS" :key="opt" :value="opt">{{ opt }}</option>
                      </select>
                    </label>
                    <label>
                      <span>Quante volte puoi allenarti in una settimana?</span>
                      <select v-model="form.allenamentoFrequenza">
                        <option value="">Seleziona…</option>
                        <option v-for="opt in FREQUENZA_ALLENAMENTO_OPTIONS" :key="opt" :value="opt">{{ opt }}</option>
                      </select>
                    </label>
                    <label>
                      <span>Durata massima di ogni seduta (minuti)</span>
                      <select v-model="form.allenamentoDurata">
                        <option value="">Seleziona…</option>
                        <option v-for="opt in DURATA_ALLENAMENTO_OPTIONS" :key="opt" :value="opt">{{ opt }}</option>
                      </select>
                    </label>
                  </div>

                  <div class="equipment-group">
                    <span class="group-label">Attrezzatura disponibile a casa</span>
                    <div class="equipment-grid">
                      <label v-for="opt in ATTREZZATURE" :key="opt" class="equipment-pill">
                        <input type="checkbox" :value="opt" v-model="form.attrezzaturaCasa" />
                        <span class="equipment-label">{{ opt }}</span>
                      </label>
                    </div>
                    <label v-if="form.attrezzaturaCasa.includes('Altro')" class="equipment-altro">
                      <span>Descrivi l’attrezzatura (Altro)</span>
                      <input
                        type="text"
                        v-model.trim="form.attrezzaturaCasaAltro"
                        placeholder="Es. cyclette, kettlebell, etc."
                      />
                    </label>
                  </div>

                  <label>
                    <span>Obiettivo del percorso</span>
                    <textarea v-model.trim="form.obiettivoPercorso" rows="3"></textarea>
                  </label>

                  <label>
                    <span>Alimenti da non introdurre</span>
                    <textarea v-model.trim="form.alimentiDaEscludere" rows="3" placeholder="Es. funghi porcini, derivati del latte…"></textarea>
                  </label>

                  <label class="checkbox-item">
                    <input type="checkbox" v-model="form.responsabilitaAlimenti" />
                    <span>Confermo la responsabilità della non introduzione degli alimenti indicati.</span>
                  </label>
                </div>
              </section>

              <!-- Misurazioni -->
              <section class="qi-block">
                <header class="qi-block__header">
                  <div class="qi-block__header-text">
                    <h2 class="qi-block__title">Misurazioni</h2>
                    <p class="qi-block__subtitle muted">
                      Circonferenza addome: 1 cm sopra l'ombelico • Coscia: metà tra anca e ginocchio • Fianchi: punto più largo dell'esterno coscia.
                    </p>
                  </div>
                </header>
                <div class="qi-block__body">
                  <div class="grid two">
                    <label>
                      <span>Misure prese in preciclo/ciclo?</span>
                      <select v-model="form.misurePreciclo">
                        <option value="">Seleziona…</option>
                        <option v-for="opt in YES_NO_OPTIONS" :key="opt" :value="opt">{{ opt }}</option>
                      </select>
                    </label>
                    <label>
                      <span>Peso (kg)</span>
                      <input v-model.trim="form.peso" type="text" inputmode="decimal" />
                    </label>
                    <label>
                      <span>Altezza (cm)</span>
                      <input v-model.trim="form.altezza" type="text" inputmode="decimal" />
                    </label>
                    <label>
                      <span>Fianchi (cm)</span>
                      <input v-model.trim="form.fianchi" type="text" inputmode="decimal" />
                    </label>
                    <label>
                      <span>Addome (cm)</span>
                      <input v-model.trim="form.addome" type="text" inputmode="decimal" />
                    </label>
                    <label>
                      <span>Torace (cm)</span>
                      <input v-model.trim="form.torace" type="text" inputmode="decimal" />
                    </label>
                    <label>
                      <span>Coscia (cm)</span>
                      <input v-model.trim="form.coscia" type="text" inputmode="decimal" />
                    </label>
                    <label>
                      <span>Braccio (cm)</span>
                      <input v-model.trim="form.braccio" type="text" inputmode="decimal" />
                    </label>
                    <label>
                      <span>Massa magra (%)</span>
                      <input v-model.trim="form.massaMagra" type="text" inputmode="decimal" />
                    </label>
                    <label>
                      <span>Massa grassa (%)</span>
                      <input v-model.trim="form.massaGrassa" type="text" inputmode="decimal" />
                    </label>
                    <label>
                      <span>Acqua corporea (%)</span>
                      <input v-model.trim="form.acquaCorporea" type="text" inputmode="decimal" />
                    </label>
                  </div>
                  <label>
                    <span>Note sulle misurazioni</span>
                    <textarea v-model.trim="form.misurazioniNote" rows="3"></textarea>
                  </label>
                </div>
              </section>

              <!-- Giampaolo Fusco -->
              <section class="qi-block qi-block--fusco">
                <header class="qi-block__header">
                  <div class="qi-block__header-text">
                    <h2 class="qi-block__title">Giampaolo Fusco</h2>
                  </div>
                </header>
                <div class="qi-block__body">
                  <div class="grid two">
                    <label>
                      <span>Età</span>
                      <input
                        type="text"
                        class="fusco-age-input"
                        :value="calculatedAgeLabel"
                        readonly
                        placeholder="Completa la data di nascita"
                      />
                    </label>
                    <label>
                      <span>Nome e cognome</span>
                      <input
                        v-model.trim="form.fuscoNomeCognome"
                        type="text"
                        placeholder="Inserisci nome e cognome"
                      />
                    </label>
                  </div>
                  <label>
                    <span>Note</span>
                    <textarea
                      v-model.trim="form.fuscoNote"
                      rows="3"
                      placeholder="Inserisci le informazioni richieste"
                    ></textarea>
                  </label>
                </div>
              </section>

              <!-- Foto -->
              <section class="qi-block">
                <header class="qi-block__header">
                  <div class="qi-block__header-text">
                    <h2 class="qi-block__title">Foto</h2>
                    <p class="photo-intro">Carica foto a figura intera, con buona luce e qualità.</p>
                  </div>
                </header>
                <div class="qi-block__body">
                  <div class="photo-grid">
                    <div class="photo-card">
                      <h3 class="photo-card__title">Foto frontale</h3>
                      <p class="photo-card__description">Braccia lungo i fianchi, gambe leggermente divaricate.</p>
                      <div class="photo-card__upload">
                        <span class="photo-card__upload-label">Carica file</span>
                        <input type="file" accept="image/*" @change="onPhotoChange('anteriore', $event)" />
                      </div>
                      <div class="photo-card__status">
                        <div v-if="uploadingPhoto.anteriore" class="upload-progress">
                          <span class="spinner"></span>
                          <span>Caricamento…</span>
                        </div>
                        <div v-else-if="form.foto.anteriore" class="file-meta">
                          <div v-if="isImageFile(form.foto.anteriore)" class="file-preview">
                            <img
                              :src="form.foto.anteriore.url"
                              alt="Anteprima foto frontale"
                              loading="lazy"
                            />
                          </div>
                          <a :href="form.foto.anteriore.url" target="_blank" rel="noopener">{{ form.foto.anteriore.name }}</a>
                          <button class="staff-button-danger" type="button" @click="removePhoto('anteriore')">Elimina</button>
                        </div>
                      </div>
                    </div>
                    <div class="photo-card">
                      <h3 class="photo-card__title">Foto posteriore</h3>
                      <p class="photo-card__description">Braccia lungo i fianchi, capelli legati o in avanti.</p>
                      <div class="photo-card__upload">
                        <span class="photo-card__upload-label">Carica file</span>
                        <input type="file" accept="image/*" @change="onPhotoChange('posteriore', $event)" />
                      </div>
                      <div class="photo-card__status">
                        <div v-if="uploadingPhoto.posteriore" class="upload-progress">
                          <span class="spinner"></span>
                          <span>Caricamento…</span>
                        </div>
                        <div v-else-if="form.foto.posteriore" class="file-meta">
                          <div v-if="isImageFile(form.foto.posteriore)" class="file-preview">
                            <img
                              :src="form.foto.posteriore.url"
                              alt="Anteprima foto posteriore"
                              loading="lazy"
                            />
                          </div>
                          <a :href="form.foto.posteriore.url" target="_blank" rel="noopener">{{ form.foto.posteriore.name }}</a>
                          <button class="staff-button-danger" type="button" @click="removePhoto('posteriore')">Elimina</button>
                        </div>
                      </div>
                    </div>
                    <div class="photo-card">
                      <h3 class="photo-card__title">Foto profilo sinistro</h3>
                      <p class="photo-card__description">Braccia avanti e tese, capelli raccolti.</p>
                      <div class="photo-card__upload">
                        <span class="photo-card__upload-label">Carica file</span>
                        <input type="file" accept="image/*" @change="onPhotoChange('profiloSinistro', $event)" />
                      </div>
                      <div class="photo-card__status">
                        <div v-if="uploadingPhoto.profiloSinistro" class="upload-progress">
                          <span class="spinner"></span>
                          <span>Caricamento…</span>
                        </div>
                        <div v-else-if="form.foto.profiloSinistro" class="file-meta">
                          <div v-if="isImageFile(form.foto.profiloSinistro)" class="file-preview">
                            <img
                              :src="form.foto.profiloSinistro.url"
                              alt="Anteprima foto profilo sinistro"
                              loading="lazy"
                            />
                          </div>
                          <a :href="form.foto.profiloSinistro.url" target="_blank" rel="noopener">{{ form.foto.profiloSinistro.name }}</a>
                          <button class="staff-button-danger" type="button" @click="removePhoto('profiloSinistro')">Elimina</button>
                        </div>
                      </div>
                    </div>
                    <div class="photo-card">
                      <h3 class="photo-card__title">Foto profilo destro</h3>
                      <p class="photo-card__description">Braccia avanti e tese, capelli raccolti.</p>
                      <div class="photo-card__upload">
                        <span class="photo-card__upload-label">Carica file</span>
                        <input type="file" accept="image/*" @change="onPhotoChange('profiloDestro', $event)" />
                      </div>
                      <div class="photo-card__status">
                        <div v-if="uploadingPhoto.profiloDestro" class="upload-progress">
                          <span class="spinner"></span>
                          <span>Caricamento…</span>
                        </div>
                        <div v-else-if="form.foto.profiloDestro" class="file-meta">
                          <div v-if="isImageFile(form.foto.profiloDestro)" class="file-preview">
                            <img
                              :src="form.foto.profiloDestro.url"
                              alt="Anteprima foto profilo destro"
                              loading="lazy"
                            />
                          </div>
                          <a :href="form.foto.profiloDestro.url" target="_blank" rel="noopener">{{ form.foto.profiloDestro.name }}</a>
                          <button class="staff-button-danger" type="button" @click="removePhoto('profiloDestro')">Elimina</button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              <!-- Idoneità -->
              <section class="qi-block">
                <header class="qi-block__header">
                  <h2 class="qi-block__title">Idoneità</h2>
                </header>
                <div class="qi-block__body idoneita-grid">
                  <div class="upload-card">
                    <span>Carica il certificato medico</span>
                    <p class="muted small-text">Accettiamo PDF o immagini. Assicurati che il certificato sia in corso di validità.</p>
                    <input type="file" accept=".pdf,image/*" @change="onIdoneitaFileChange('certificatoMedicoFile', $event)" />
                    <div v-if="uploadingIdoneita.certificatoMedicoFile" class="upload-progress">
                      <span class="spinner"></span>
                      <span>Caricamento…</span>
                    </div>
                    <div v-else-if="form.idoneita.certificatoMedicoFile" class="file-meta">
                      <div v-if="isImageFile(form.idoneita.certificatoMedicoFile)" class="file-preview">
                        <img
                          :src="form.idoneita.certificatoMedicoFile.url"
                          alt="Anteprima certificato medico"
                          loading="lazy"
                        />
                      </div>
                      <div
                        v-else-if="isPdfFile(form.idoneita.certificatoMedicoFile)"
                        class="file-preview file-preview--pdf"
                      >
                        <iframe
                          :src="form.idoneita.certificatoMedicoFile.url"
                          title="Anteprima certificato PDF"
                          loading="lazy"
                        ></iframe>
                      </div>
                      <a :href="form.idoneita.certificatoMedicoFile.url" target="_blank" rel="noopener">{{ form.idoneita.certificatoMedicoFile.name }}</a>
                      <button class="staff-button-danger" type="button" @click="removeIdoneitaFile('certificatoMedicoFile')">Elimina</button>
                    </div>
                  </div>

                  <div class="autocert-card">
                    <h3>Completa la tua autocertificazione</h3>
                    <p class="muted small-text">
                      Inserisci i tuoi dati nei campi indicati e mantieni aggiornato questo testo: sarà salvato direttamente all’interno del questionario.
                    </p>
                    <textarea
                      v-model.trim="form.idoneita.autocertificazioneTesto"
                      rows="6"
                      class="autocert-textarea"
                    ></textarea>

                    <div v-if="form.idoneita.autocertificazioneFile" class="file-meta legacy-autocert">
                      <span class="muted">Autocertificazione caricata in precedenza:</span>
                      <div v-if="isImageFile(form.idoneita.autocertificazioneFile)" class="file-preview">
                        <img
                          :src="form.idoneita.autocertificazioneFile.url"
                          alt="Anteprima autocertificazione"
                          loading="lazy"
                        />
                      </div>
                      <div
                        v-else-if="isPdfFile(form.idoneita.autocertificazioneFile)"
                        class="file-preview file-preview--pdf"
                      >
                        <iframe
                          :src="form.idoneita.autocertificazioneFile.url"
                          title="Anteprima autocertificazione PDF"
                          loading="lazy"
                        ></iframe>
                      </div>
                      <a :href="form.idoneita.autocertificazioneFile.url" target="_blank" rel="noopener">{{ form.idoneita.autocertificazioneFile.name }}</a>
                      <button class="staff-button-danger" type="button" @click="removeIdoneitaFile('autocertificazioneFile')">Elimina</button>
                    </div>
                  </div>
                </div>
              </section>

              <!-- Privacy -->
              <section class="qi-block">
                <header class="qi-block__header">
                  <h2 class="qi-block__title">Privacy</h2>
                </header>
                <div class="qi-block__body">
                  <label class="checkbox-item">
                    <input type="checkbox" v-model="form.privacy.datiPersonali" />
                    <span>Presto il consenso al trattamento dei dati personali (obbligatorio).</span>
                  </label>
                  <label class="checkbox-item">
                    <input type="checkbox" v-model="form.privacy.datiParticolari" />
                    <span>Presto il consenso al trattamento dei dati relativi alla salute (obbligatorio).</span>
                  </label>
                  <label class="checkbox-item">
                    <input type="checkbox" v-model="form.privacy.marketing" />
                    <span>Presto il consenso al trattamento dei dati per finalità informative e promozionali (facoltativo).</span>
                  </label>
                  <label class="checkbox-item">
                    <input type="checkbox" v-model="form.privacy.comunicazione" />
                    <span>Presto il consenso alla comunicazione dei dati a studi medici accreditati (facoltativo).</span>
                  </label>
                </div>
              </section>

              <!-- Termini -->
              <section class="qi-block">
                <header class="qi-block__header">
                  <h2 class="qi-block__title">Termini e condizioni</h2>
                </header>
                <div class="qi-block__body">
                  <label class="checkbox-item">
                    <input type="checkbox" v-model="form.accettoTermini" />
                    <span>Dichiaro di aver letto e accettato i termini e le condizioni.</span>
                  </label>
                </div>
              </section>

              <footer class="form-footer">
                <div class="left">
                  <button type="submit" class="primary-button staff-button-positive" :disabled="saving || loading">
                    {{ saving ? 'Salvataggio…' : 'Crea bozza' }}
                  </button>
                  <button
                    type="button"
                    class="secondary-button staff-button-cancel"
                    :disabled="saving || loading || !dirty"
                    @click="resetForm"
                  >
                    Annulla modifiche
                  </button>
                  <button
                    type="button"
                    class="primary-button primary-button--success"
                    :disabled="saving || loading || completingStep"
                    @click="completeQuestionarioStep"
                  >
                    {{ completingStep ? 'Segna…' : 'Metti in lavorazione' }}
                  </button>
                </div>
                <div class="right">
                  <span v-if="dirty" class="dirty-indicator">Modifiche non salvate</span>
                </div>
              </footer>
            </form>
          </div>
        </div>
      </div>
</template>

<script>
import { computed, nextTick, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  getFirestore,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from 'firebase/firestore';
import {
  deleteObject,
  getDownloadURL,
  getStorage,
  ref as storageRef,
  uploadBytesResumable,
} from 'firebase/storage';
import { getAuth, onAuthStateChanged, signInWithCustomToken } from 'firebase/auth';
import { dispatchNeedsUpdateNotification } from '../../lib/percorsoStepNotifications';
import { setStepDone } from '@/lib/percorsi';
import { buildQuestionarioPdfFileName } from '../../lib/pdfExport';
import { generateQuestionarioPdf } from '@/lib/programmiPdf';


const QUESTIONARI_COLLECTION = 'questionari';
const QUESTIONARIO_TYPE = 'user_programmi_questionario';
const QUESTIONARIO_TEMPLATE = {
  id: 'user_programmi_questionario_v1',
  key: 'user_programmi_questionario',
  label: 'Questionario Programmi',
  version: 1,
};

const TURNISTA_OPTIONS = ['Si', 'No', 'Turni notturni'];
const WORK_HOURS_OPTIONS = ['Non lavoro', 'Studio', '3', '4', '5', '6', '7', '8', '9', '10', '+10'];
const LIFESTYLE_OPTIONS = ['Sedentario', 'Dinamico'];
const SLEEP_HOURS_OPTIONS = ['2', '3', '4', '5', '6', '7', '8', '9', '10', '+10'];
const SLEEP_QUALITY_OPTIONS = ['Regolare', 'Non regolare', 'Insonnia'];
const YES_NO_OPTIONS = ['Si', 'No'];
const ALLENAMENTO_ATTUALE_OPTIONS = ['Si', 'No', 'Sono ferma da più di un anno'];
const TIPI_ALLENAMENTO = [
  'Allenamento con Pesi',
  'Corpo Libero',
  'Pilates / yoga / posturale',
  'Altri corsi fitness',
  'Corsa',
  'Nuoto',
  'Altro',
];
const ESPERIENZA_SALA_OPTIONS = ['Principiante', 'Intermedio', 'Esperto'];
const PT_OPTIONS = ['Si', 'No', 'No, però mi piacerebbe'];
const ALVO_OPTIONS = ['Regolare', 'Stitico', 'Scariche intestinali'];
const CICLO_MESTRUALE_OPTIONS = [
  'Regolare',
  'Regolare con pillola',
  'Non regolare',
  'Gravidanza in corso',
  'Post parto con allattamento',
  'Post parto senza allattamento',
  'Menopausa',
  'Amenorrea',
];
const ACQUA_OPTIONS = ['1', '2', '3', '4', '4+'];
const ALCOL_OPTIONS = ['Astemio', 'Saltuariamente', 'Solo nei weekend', 'Solo ai pasti', 'Tutti i giorni'];
const DIAGNOSIS_OPTIONS = ['Diagnosi certificata', 'Sospetta o non certificata'];
const REGIME_ALIMENTARE_OPTIONS = ['Onnivoro', 'Vegetariano', 'Vegano'];
const PASTI_OPTIONS = ['1', '2', '3', '4', '5', '5+'];
const SALTARE_PASTI_OPTIONS = ['Mai', 'Raramente', 'Spesso', 'Molto spesso'];
const LUOGO_ALLENAMENTO_OPTIONS = ['Home', 'Gym', 'Sia home che gym'];
const FREQUENZA_ALLENAMENTO_OPTIONS = ['1', '2', '3', '4', '5', '5+'];
const DURATA_ALLENAMENTO_OPTIONS = ['45', '50', '60', '90', '90+'];
const MONTH_OPTIONS = [
  { value: '01', label: '01 - Gennaio' },
  { value: '02', label: '02 - Febbraio' },
  { value: '03', label: '03 - Marzo' },
  { value: '04', label: '04 - Aprile' },
  { value: '05', label: '05 - Maggio' },
  { value: '06', label: '06 - Giugno' },
  { value: '07', label: '07 - Luglio' },
  { value: '08', label: '08 - Agosto' },
  { value: '09', label: '09 - Settembre' },
  { value: '10', label: '10 - Ottobre' },
  { value: '11', label: '11 - Novembre' },
  { value: '12', label: '12 - Dicembre' },
];

const DAY_OPTIONS = Array.from({ length: 31 }, (_, idx) => {
  const value = String(idx + 1).padStart(2, '0');
  return { value, label: value };
});

const buildYearOptions = () => {
  const now = new Date();
  const maxYear = now.getFullYear() - 10;
  const minYear = now.getFullYear() - 90;
  const list = [];
  for (let year = maxYear; year >= minYear; year -= 1) {
    list.push(String(year));
  }
  return list;
};

const YEAR_OPTIONS = buildYearOptions();

const ATTREZZATURE = [
  'Fitball',
  'Glute band',
  'Cavigliere',
  'Manubri',
  'Corda',
  'Bilanciere',
  'TRX',
  'Panca',
  'Elastici di varia resistenza',
  'Altro',
];
const AUTOCERTIFICAZIONE_TEMPLATE = `Autocertificazione
Io sottoscritta/o ______________________ , nata/o a _______________ (____), il __/__/______ e residente a ___________ in via ____________________ n° _____ , dichiaro sotto la mia responsabilità di essere in buona salute ed idonea/o alla pratica dell'attività fisica NON agonistica.`;

const PATHOLOGIES = [
  { key: 'curaFertilita', label: 'Cura fertilità' },
  { key: 'diabete', label: 'Diabete' },
  { key: 'endometriosi', label: 'Endometriosi' },
  { key: 'insulinoResistenza', label: 'Insulino resistenza' },
  { key: 'ipertiroidismo', label: 'Ipertiroidismo' },
  { key: 'ipotiroidismo', label: 'Ipotiroidismo' },
  { key: 'ovaioPolicistico', label: 'Ovaio policistico' },
  { key: 'patologieCardiache', label: 'Patologie cardiache' },
  { key: 'fibromialgia', label: 'Fibromialgia' },
  { key: 'patologieRenali', label: 'Patologie renali' },
  { key: 'tiroideHashimoto', label: 'Tiroide Hashimoto' },
  { key: 'obesita', label: 'Obesità' },
  { key: 'reflussoGastrico', label: 'Reflusso gastrico' },
  { key: 'gastrite', label: 'Gastrite' },
  { key: 'sindromeColonIrr', label: 'Sindrome del colon irritabile (IBS)' },
  { key: 'dca', label: 'Disturbi del comportamento alimentare (DCA)' },
];

const MUSCLE_PATHOLOGIES = [
  { key: 'diastasiAddominale', label: 'Diastasi addominale', askCm: true },
  { key: 'schiena', label: 'Schiena' },
  { key: 'collo', label: 'Collo' },
  { key: 'spalla', label: 'Spalla' },
  { key: 'gomito', label: 'Gomito' },
  { key: 'ginocchio', label: 'Ginocchio' },
  { key: 'caviglia', label: 'Caviglia' },
  { key: 'bacino', label: 'Bacino' },
];

const INVALID_STORAGE_CHARS = /[\\/?%*:|"<>#[\]]/g;
const sanitizeName = (name = '') => {
  const cleaned = name.replace(INVALID_STORAGE_CHARS, '').replace(/\s+/g, '_');
  let result = '';
  for (const ch of cleaned) {
    if (ch.charCodeAt(0) >= 32) result += ch;
  }
  return result;
};

const PORTAL_BASE_URL = 'https://www.metodofusco.it/tickets';

const deepClone = (value) => JSON.parse(JSON.stringify(value ?? null));

const assignDeep = (target, source = {}) => {
  Object.keys(target).forEach((key) => {
    const targetValue = target[key];
    const sourceValue = source[key];

    if (Array.isArray(targetValue)) {
      target[key] = Array.isArray(sourceValue) ? deepClone(sourceValue) : [];
    } else if (targetValue && typeof targetValue === 'object') {
      assignDeep(targetValue, sourceValue || {});
    } else {
      target[key] = sourceValue !== undefined ? sourceValue : targetValue;
    }
  });
};

const toDateSafe = (value) => {
  if (!value) return null;
  if (typeof value.toDate === 'function') {
    const d = value.toDate();
    return Number.isNaN(d.getTime()) ? null : d;
  }
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value;
  if (typeof value === 'string' || typeof value === 'number') {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return null;
};

const formatDateUtils = (value) => {
  const date = toDateSafe(value);
  if (!date) return '';
  const formatter = new Intl.DateTimeFormat('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
  return formatter.format(date);
};

const calculateAgeFromBirthdate = (value) => {
  let normalized = value;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    const match = trimmed.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
    if (match) {
      const [, d, m, y] = match;
      normalized = `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    }
  }

  const birthDate = toDateSafe(normalized);
  if (!birthDate) return null;
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  const dayDiff = today.getDate() - birthDate.getDate();
  if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
    age -= 1;
  }
  return age >= 0 ? age : null;
};

const defaultPathologyState = () => ({
  presente: false,
  diagnosi: '',
  certificazioneFile: null,
  note: '',
});

const defaultMuscoloState = (askCm = false) => ({
  presente: false,
  diagnosi: '',
  certificazioneFile: null,
  note: '',
  cm: askCm ? '' : null,
});

const defaultForm = () => ({
  dataNascita: '',
  professione: '',
  turnista: '',
  oreLavorative: '',
  stileVita: '',
  oreSonno: '',
  qualitaSonno: '',
  periodoStressante: '',
  allenamentoAttuale: '',
  tipiAllenamento: [],
  esperienzaSala: '',
  ptPresenza: '',
  ptVideocall: '',
  alvo: '',
  cicloMestruale: '',
  gravidanze: [],
  litriAcqua: '',
  fumo: '',
  bevandeAlcoliche: '',
  patologie: PATHOLOGIES.reduce((acc, item) => {
    acc[item.key] = defaultPathologyState();
    return acc;
  }, {}),
  patologieAltre: [],
  haPatologieMetaboliche: 'No',
  dichiarazionePatologie: false,
  patologieMuscolo: MUSCLE_PATHOLOGIES.reduce((acc, item) => {
    acc[item.key] = defaultMuscoloState(item.askCm);
    return acc;
  }, {}),
  patologieMuscoloAltre: [],
  haPatologieMuscolo: 'No',
  infortuni: [],
  interventi: [],
  noteLimitazioni: '',
  farmaciAssunti: '',
  regimeAlimentare: '',
  regimiControllatiPassato: '',
  regimeControllato: '',
  pastiGiorno: '',
  saltarePasti: '',
  diarioColazione: '',
  diarioMetaMattina: '',
  diarioPranzo: '',
  diarioMetaPomeriggio: '',
  diarioCena: '',
  integratoriUsati: '',
  integratoriUsatiQuali: '',
  integratoriAttuali: '',
  integratoriAttualiQuali: '',
  allenamentoLuogo: '',
  attrezzaturaCasa: [],
  attrezzaturaCasaAltro: '',
  allenamentoFrequenza: '',
  allenamentoDurata: '',
  obiettivoPercorso: '',
  alimentiDaEscludere: '',
  responsabilitaAlimenti: false,
  misurePreciclo: '',
  peso: '',
  altezza: '',
  fianchi: '',
  addome: '',
  torace: '',
  coscia: '',
  braccio: '',
  massaMagra: '',
  massaGrassa: '',
  acquaCorporea: '',
  misurazioniNote: '',
  fuscoNomeCognome: '',
  fuscoNote: '',
  foto: {
    anteriore: null,
    posteriore: null,
    profiloSinistro: null,
    profiloDestro: null,
  },
  idoneita: {
    autocertificazioneFile: null,
    certificatoMedicoFile: null,
    autocertificazioneTesto: AUTOCERTIFICAZIONE_TEMPLATE,
  },
  privacy: {
    datiPersonali: false,
    datiParticolari: false,
    marketing: false,
    comunicazione: false,
  },
  accettoTermini: false,
});

const makeStoragePath = (uid, docId, ...segments) =>
  ['documenti', uid, 'questionari', docId, ...segments].filter(Boolean).join('/');

export default {
  name: 'UserProgrammiQuestionario',
  props: {
    userId: {
      type: String,
      required: true,
    },
  },
  setup(props) {
    const route = useRoute();
    const router = useRouter();
    const db = getFirestore();
    const storage = getStorage();
    const auth = getAuth();

    const pickQueryValue = (key) => {
      const raw = route.query?.[key];
      if (Array.isArray(raw)) return raw[0] ?? null;
      return raw ?? null;
    };

    const toTrimmedStringOrNull = (value) => {
      if (value === undefined || value === null) return null;
      const str = typeof value === 'string' ? value : String(value);
      const trimmed = str.trim();
      return trimmed ? trimmed : null;
    };

    const toStepIndex = (value) => {
      const num = Number(value);
      if (!Number.isFinite(num)) return null;
      const intVal = Math.round(num);
      return intVal >= 0 ? intVal : null;
    };

    const percorsoContext = computed(() => {
      const stepIndex = toStepIndex(pickQueryValue('stepIndex'));
      const sequenceIndex = toStepIndex(pickQueryValue('sequenceIndex'));
      const sequenceTotal = toStepIndex(pickQueryValue('sequenceTotal'));
      return {
        percorsoId: toTrimmedStringOrNull(pickQueryValue('percorsoId')),
        stepIndex,
        stepKey: toTrimmedStringOrNull(pickQueryValue('stepKey')) || 'questionario',
        sequenceGroupId: toTrimmedStringOrNull(pickQueryValue('sequenceGroupId')),
        sequenceIndex,
        sequenceTotal,
        productId: toTrimmedStringOrNull(pickQueryValue('productId')),
        productName: toTrimmedStringOrNull(pickQueryValue('productName')),
        programTitle: toTrimmedStringOrNull(pickQueryValue('programTitle')),
        programLabel: toTrimmedStringOrNull(pickQueryValue('programLabel')),
        plannedStartAt: toTrimmedStringOrNull(pickQueryValue('plannedStartAt')),
        expireAt: toTrimmedStringOrNull(pickQueryValue('expireAt')),
        stepName: toTrimmedStringOrNull(pickQueryValue('stepName')),
        orderRef: toTrimmedStringOrNull(pickQueryValue('orderRef')),
      };
    });

    const form = reactive(defaultForm());
    const birthdateParts = reactive({ day: '', month: '', year: '' });
    const printableCard = ref(null);
    const lastLoaded = ref(deepClone(form));
    const questionarioDocId = ref('');
    const preferredDocId = computed(() => {
      const candidates = [
        pickQueryValue('questionarioDocId'),
        pickQueryValue('questionarioUid'),
        pickQueryValue('questionnaireDocId'),
        pickQueryValue('questionnaireId'),
        pickQueryValue('docId'),
      ];
      for (const raw of candidates) {
        const value = toTrimmedStringOrNull(raw);
        if (value) return value;
      }
      return null;
    });
    const questionarioInfo = ref({ createdAt: null, updatedAt: null, templateLabel: QUESTIONARIO_TEMPLATE.label });
    const questionarioPartial = ref(false);
    const manualDocLinkState = reactive({
      shouldDetach: false,
      docId: '',
      snapshot: null,
    });
    const customToken = computed(() => toTrimmedStringOrNull(pickQueryValue('customToken')));
    const manualStandaloneView = computed(() => {
      const raw = pickQueryValue('manualStandalone');
      if (raw == null) return false;
      const value = String(raw).trim().toLowerCase();
      return value === '1' || value === 'true';
    });
    const resetManualDocLinkState = () => {
      manualDocLinkState.shouldDetach = false;
      manualDocLinkState.docId = '';
      manualDocLinkState.snapshot = null;
    };
    const evaluateManualDocLinkState = (docId, data) => {
      const ctx = percorsoContext.value;
      const hasPercorsoContext = !!ctx.percorsoId && Number.isInteger(ctx.stepIndex);
      if (!manualStandaloneView.value && hasPercorsoContext && data?.manualStandalone) {
        manualDocLinkState.shouldDetach = true;
        manualDocLinkState.docId = docId;
        manualDocLinkState.snapshot = data;
      } else {
        resetManualDocLinkState();
      }
    };
    const partialSaving = ref(false);
    const pdfExporting = ref(false);
    const copyingAnswers = ref(false);
    const pastingAnswers = ref(false);
    const userDisplayName = computed(() => {
      const name = toTrimmedStringOrNull(pickQueryValue('userName'));
      const surname = toTrimmedStringOrNull(pickQueryValue('userSurname'));
      return [name, surname].filter(Boolean).join(' ').trim();
    });
    const userProfile = ref({ name: '', surname: '' });

    const calculatedAgeLabel = computed(() => {
      const years = calculateAgeFromBirthdate(form.dataNascita);
      return years != null ? `${years} anni` : '';
    });

    const adminState = reactive({
      status: 'none',
      note: '',
      updatedAt: null,
    });
    const adminLoading = ref(false);
    const adminSaving = ref(false);
    const adminError = ref('');
    const needsUpdateOpen = ref(false);
    const needsUpdateNote = ref('');
    let adminStateRequestId = 0;

    const loading = ref(true);
    const saving = ref(false);
    const completingStep = ref(false);
    const dirty = ref(false);
    const errorMessage = ref('');
    const toasts = ref([]);
    const toastTimers = new Map();

    const suppressDirty = ref(false);

    const uploadingPhoto = reactive({});
    const uploadingCertification = reactive({});
    const uploadingIdoneita = reactive({});

    const setUploadState = (store, key, value) => {
      store[key] = value;
    };

    const certificationUploadKey = (group, identifier) => `${group}__${identifier}`;
    const isCertificationUploading = (group, identifier) =>
      !!uploadingCertification[certificationUploadKey(group, identifier)];

    const isImageFile = (metadata) => {
      if (!metadata) return false;
      const type = String(metadata.contentType || '').toLowerCase();
      if (type.startsWith('image/')) return true;
      const name = String(metadata.name || '').toLowerCase();
      return /\.(png|jpe?g|gif|webp|bmp|svg)$/.test(name);
    };

    const isPdfFile = (metadata) => {
      if (!metadata) return false;
      const type = String(metadata.contentType || '').toLowerCase();
      if (type === 'application/pdf') return true;
      const name = String(metadata.name || '').toLowerCase();
      return name.endsWith('.pdf');
    };

    const currentUserId = ref(
      props.userId ||
      route.params.userId ||
      toTrimmedStringOrNull(route.query?.userId) ||
      ''
    );
    const effectiveUserId = computed(() => currentUserId.value || auth.currentUser?.uid || '');
    const questionarioTargetKey = computed(() => {
      const ctx = percorsoContext.value;
      const docId = preferredDocId.value || questionarioDocId.value || '';
      return [
        effectiveUserId.value || '',
        ctx.percorsoId || '',
        Number.isInteger(ctx.stepIndex) ? String(ctx.stepIndex) : 'x',
        ctx.stepKey || '',
        docId,
      ].join('__');
    });
    let authUnsubscribe = null;

    const canManageAdmin = computed(() => {
      const ctx = percorsoContext.value;
      return !!ctx.percorsoId && Number.isInteger(ctx.stepIndex);
    });

    const adminStatusLabel = computed(() => {
      if (adminState.status === 'approved') return 'Approvato';
      if (adminState.status === 'needs_update') return 'Richiesto aggiornamento';
      return '—';
    });

    const adminChipClass = computed(() => {
      if (adminState.status === 'approved') return 'chip-green';
      if (adminState.status === 'needs_update') return 'chip-orange';
      return 'chip-neutral';
    });

    const dismissToast = (id) => {
      toasts.value = toasts.value.filter((toast) => toast.id !== id);
      const timer = toastTimers.get(id);
      if (timer) {
        clearTimeout(timer);
        toastTimers.delete(id);
      }
    };

    const pushToast = (message, type = 'info') => {
      if (!message) return;
      const id = `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
      toasts.value.push({ id, message, type });
      const timer = setTimeout(() => dismissToast(id), 4200);
      toastTimers.set(id, timer);
    };

    const showActionMessage = (message, type = 'success') => {
      if (!message) return;
      pushToast(message, type);
    };

    const assignAdminState = (step = {}) => {
      adminState.status = step?.adminStatus || 'none';
      adminState.note = step?.adminNote || '';
      adminState.updatedAt = step?.adminUpdatedAt ? toDateSafe(step.adminUpdatedAt) : null;
    };

    const refreshAdminState = async () => {
      const ctx = percorsoContext.value;
      const idx = Number.isInteger(ctx.stepIndex) ? ctx.stepIndex : null;
      needsUpdateOpen.value = false;
      if (!ctx.percorsoId || idx == null) {
        assignAdminState({});
        needsUpdateNote.value = '';
        adminError.value = '';
        adminLoading.value = false;
        return;
      }

      const requestId = ++adminStateRequestId;
      adminLoading.value = true;
      adminError.value = '';
      try {
        const percorsoRef = doc(db, 'percorsi', ctx.percorsoId);
        const snap = await getDoc(percorsoRef);
        if (!snap.exists()) {
          if (requestId === adminStateRequestId) {
            assignAdminState({});
            adminError.value = 'Percorso non trovato.';
          }
          return;
        }
        const data = snap.data() || {};
        const steps = Array.isArray(data.steps) ? data.steps : [];
        const step = steps[idx] || {};
        if (requestId === adminStateRequestId) {
          assignAdminState(step);
          needsUpdateNote.value = step.adminNote || '';
        }
      } catch (error) {
        console.error('Errore caricando lo stato admin del questionario:', error);
        if (requestId === adminStateRequestId) {
          adminError.value = 'Impossibile recuperare lo stato admin.';
        }
      } finally {
        if (requestId === adminStateRequestId) {
          adminLoading.value = false;
        }
      }
    };

    const mutatePercorsoStep = async (mutator) => {
      const ctx = percorsoContext.value;
      const idx = Number.isInteger(ctx.stepIndex) ? ctx.stepIndex : null;
      if (!ctx.percorsoId || idx == null) {
        throw new Error('Step non collegato a un percorso valido.');
      }
      const percorsoRef = doc(db, 'percorsi', ctx.percorsoId);
      const snap = await getDoc(percorsoRef);
      if (!snap.exists()) {
        throw new Error('Percorso non trovato.');
      }
      const data = snap.data() || {};
      const steps = Array.isArray(data.steps) ? [...data.steps] : [];
      const prev = steps[idx] ? { ...steps[idx] } : {};
      const next = mutator({ ...prev }) || prev;
      steps[idx] = next;
      await updateDoc(percorsoRef, { steps });
      return next;
    };

    const handleAdminApprove = async () => {
      if (!canManageAdmin.value) {
        showActionMessage('Collega il questionario a un percorso per aggiornare lo stato admin.', 'error');
        return;
      }
      adminSaving.value = true;
      try {
        const nextStep = await mutatePercorsoStep((prev) => ({
          ...prev,
          adminStatus: 'approved',
          adminNote: null,
          adminUpdatedAt: new Date().toISOString(),
        }));
        assignAdminState(nextStep);
        needsUpdateOpen.value = false;
        needsUpdateNote.value = '';
        showActionMessage('Questionario approvato.');
      } catch (error) {
        console.error('setAdminApproved error:', error);
        showActionMessage('Errore nel salvataggio dello stato admin.', 'error');
      } finally {
        adminSaving.value = false;
      }
    };

    const toggleNeedsUpdate = () => {
      if (!canManageAdmin.value) {
        showActionMessage('Collega il questionario a un percorso per richiedere aggiornamenti.', 'error');
        return;
      }
      needsUpdateOpen.value = !needsUpdateOpen.value;
      if (needsUpdateOpen.value) {
        needsUpdateNote.value = adminState.note || '';
      }
    };

    const saveNeedsUpdateRequest = async () => {
      if (!canManageAdmin.value) {
        showActionMessage('Collega il questionario a un percorso per richiedere aggiornamenti.', 'error');
        return;
      }
      const note = (needsUpdateNote.value || '').trim();
      adminSaving.value = true;
      try {
        const nextStep = await mutatePercorsoStep((prev) => ({
          ...prev,
          adminStatus: 'needs_update',
          adminNote: note || null,
          adminUpdatedAt: new Date().toISOString(),
        }));
        assignAdminState(nextStep);
        needsUpdateOpen.value = false;
        showActionMessage('Richiesta di aggiornamento salvata.');
        if (props.userId && percorsoContext.value.percorsoId) {
          try {
            await dispatchNeedsUpdateNotification({
              db,
              userUid: props.userId,
              programId: percorsoContext.value.percorsoId,
              stepKey: percorsoContext.value.stepKey || 'questionario',
              step: nextStep,
              stepIndex: percorsoContext.value.stepIndex ?? null,
              programTitle: percorsoContext.value.programTitle || percorsoContext.value.programLabel || '',
              productName: percorsoContext.value.productName || '',
              sequenceIndex: percorsoContext.value.sequenceIndex ?? null,
              sequenceTotal: percorsoContext.value.sequenceTotal ?? null,
              adminNote: note,
              notifyUser: true,
              preserveOpenStatus: true,
              awaitNotification: false,
            });
          } catch (notifyErr) {
            console.error('dispatchNeedsUpdateNotification error', notifyErr);
          }
        }
      } catch (error) {
        console.error('saveNeedsUpdate error:', error);
        showActionMessage('Errore nel salvataggio della richiesta di aggiornamento.', 'error');
      } finally {
        adminSaving.value = false;
      }
    };

    const buildShareQuery = () => {
      const ctx = percorsoContext.value;
      const query = {};
      const assign = (key, value) => {
        if (value === undefined || value === null) return;
        const str = typeof value === 'string' ? value : String(value);
        if (!str) return;
        query[key] = str;
      };
      assign('percorsoId', ctx.percorsoId);
      if (Number.isInteger(ctx.stepIndex)) assign('stepIndex', ctx.stepIndex);
      assign('stepKey', ctx.stepKey);
      assign('userId', props.userId);
      const docId = questionarioDocId.value || manualDocLinkState.docId || '';
      assign('questionarioDocId', docId);
      const isManual = manualStandaloneView.value || !!manualDocLinkState.snapshot?.manualStandalone;
      if (isManual) assign('manualStandalone', '1');
      return query;
    };

    const buildShareLink = () => {
      const params = new URLSearchParams(buildShareQuery()).toString();
      const base = PORTAL_BASE_URL.replace(/\/$/, '');
      const hashPath = '/#/user-programmi/questionario';
      return params ? `${base}${hashPath}?${params}` : `${base}${hashPath}`;
    };

    const copyFormLink = async () => {
      try {
        const link = buildShareLink();
        if (!link) throw new Error('Link non disponibile');
        if (navigator?.clipboard?.writeText) {
          await navigator.clipboard.writeText(link);
        } else if (typeof document !== 'undefined') {
          const textarea = document.createElement('textarea');
          textarea.value = link;
          textarea.setAttribute('readonly', '');
          textarea.style.position = 'absolute';
          textarea.style.left = '-9999px';
          document.body.appendChild(textarea);
          textarea.select();
          document.execCommand('copy');
          document.body.removeChild(textarea);
        } else {
          throw new Error('Clipboard non supportata');
        }
        showActionMessage('Link copiato negli appunti.');
      } catch (err) {
        console.error('copyFormLink error:', err);
        showActionMessage('Impossibile copiare il link.', 'error');
      }
    };

    const togglePartialQuestionario = async () => {
      if (partialSaving.value) return;
      partialSaving.value = true;
      try {
        const docId = await ensureQuestionarioDoc();
        const nextValue = !questionarioPartial.value;
        await updateDoc(doc(db, QUESTIONARI_COLLECTION, docId), {
          allowPartial: nextValue,
          updatedAt: serverTimestamp(),
        });
        questionarioPartial.value = nextValue;
        showActionMessage(
          nextValue
            ? 'Questionario reso parziale: foto e misure non sono obbligatorie.'
            : 'Modalità parziale disattivata: tornano obbligatorie foto e misure.'
        );
      } catch (error) {
        console.error('togglePartialQuestionario error:', error);
        showActionMessage('Impossibile aggiornare la modalità parziale.', 'error');
      } finally {
        partialSaving.value = false;
      }
    };

    const exportFormAsPdf = async () => {
      if (pdfExporting.value) return;
      pdfExporting.value = true;
      showActionMessage('Sto generando il PDF…');
      try {
        const fileName = buildQuestionarioPdfFileName({
          prefix: 'questionario_iniziale',
          userId: effectiveUserId.value || questionarioDocId.value || 'utente',
          updatedAt: questionarioInfo.value.updatedAt,
        });
        await generateQuestionarioPdf({
          answers: plainAnswers(),
          meta: {
            userId: effectiveUserId.value || questionarioDocId.value || 'utente',
            userLabel: userDisplayName.value,
            questionarioInfo: questionarioInfo.value,
            percorso: percorsoContext.value,
          },
          fileName,
        });
        showActionMessage('PDF salvato nei download.');
      } catch (err) {
        console.error('exportFormAsPdf error:', err);
        showActionMessage('Impossibile generare il PDF.', 'error');
      } finally {
        pdfExporting.value = false;
      }
    };

    const writeTextToClipboard = async (text) => {
      if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        return;
      }
      if (typeof document === 'undefined') throw new Error('Clipboard non supportata');
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.setAttribute('readonly', '');
      textarea.style.position = 'absolute';
      textarea.style.left = '-9999px';
      document.body.appendChild(textarea);
      textarea.select();
      const copied = document.execCommand('copy');
      document.body.removeChild(textarea);
      if (!copied) throw new Error('Impossibile copiare negli appunti');
    };

    const readTextFromClipboard = async () => {
      if (typeof navigator !== 'undefined' && navigator.clipboard?.readText) {
        return navigator.clipboard.readText();
      }
      if (typeof window !== 'undefined') {
        const manual = window.prompt('Incolla qui il questionario copiato e premi OK:');
        if (manual === null) throw new Error('clipboard_read_cancelled');
        return manual;
      }
      throw new Error('Clipboard non supportata');
    };

    const plainAnswers = () => deepClone(form);

    const buildClipboardPayload = () => {
      const info = questionarioInfo.value || {};
      const template = {
        id: QUESTIONARIO_TEMPLATE.id,
        key: QUESTIONARIO_TEMPLATE.key,
        label: QUESTIONARIO_TEMPLATE.label,
        version: QUESTIONARIO_TEMPLATE.version,
      };
      const health = buildHealthSnapshot();
      return {
        type: QUESTIONARIO_TYPE,
        template,
        questionarioDocId: questionarioDocId.value || null,
        userId: effectiveUserId.value || null,
        exportedAt: new Date().toISOString(),
        metadata: buildQuestionarioMetadata(),
        questionarioInfo: {
          createdAt: toIsoString(info.createdAt),
          updatedAt: toIsoString(info.updatedAt),
        },
        answers: plainAnswers(),
        ...health,
      };
    };

    const applyClipboardPayload = (payload) => {
      if (!payload || typeof payload !== 'object') {
        throw new Error('Il contenuto copiato non è valido.');
      }
      if (payload.type && payload.type !== QUESTIONARIO_TYPE) {
        throw new Error('I dati copiati appartengono a un altro questionario.');
      }
      if (!payload.answers || typeof payload.answers !== 'object') {
        throw new Error('Il contenuto copiato non contiene risposte.');
      }
      const previousBaseline = deepClone(lastLoaded.value);
      setFormData(payload.answers, payload);
      lastLoaded.value = previousBaseline;
      dirty.value = true;
      showActionMessage('Questionario incollato. Salva per confermare.');
    };

    const copyQuestionarioAnswers = async () => {
      if (copyingAnswers.value) return;
      copyingAnswers.value = true;
      try {
        const payload = buildClipboardPayload();
        const serialized = JSON.stringify(payload, null, 2);
        await writeTextToClipboard(serialized);
        showActionMessage('Questionario copiato negli appunti.');
      } catch (error) {
        console.error('copyQuestionarioAnswers error:', error);
        showActionMessage('Impossibile copiare il questionario.', 'error');
      } finally {
        copyingAnswers.value = false;
      }
    };

    const pasteQuestionarioAnswers = async () => {
      if (pastingAnswers.value) return;
      pastingAnswers.value = true;
      try {
        const rawText = await readTextFromClipboard();
        const trimmed = (rawText || '').trim();
        if (!trimmed) throw new Error('Gli appunti sono vuoti.');
        let payload;
        try {
          payload = JSON.parse(trimmed);
        } catch (parseError) {
          throw new Error('Il contenuto copiato non è un JSON valido.');
        }
        applyClipboardPayload(payload);
      } catch (error) {
        if (error?.message === 'clipboard_read_cancelled') {
          // Operazione annullata dall'utente, nessun messaggio.
        } else {
          console.error('pasteQuestionarioAnswers error:', error);
          showActionMessage(error.message || 'Impossibile incollare il questionario.', 'error');
        }
      } finally {
        pastingAnswers.value = false;
      }
    };

    const buildQuestionarioMetadata = () => {
      const ctx = percorsoContext.value;
      const meta = {
        percorsoId: ctx.percorsoId || null,
        stepIndex: Number.isInteger(ctx.stepIndex) ? ctx.stepIndex : null,
        stepKey: ctx.stepKey || null,
        sequenceGroupId: ctx.sequenceGroupId || null,
        sequenceIndex: Number.isInteger(ctx.sequenceIndex) ? ctx.sequenceIndex : null,
        sequenceTotal: Number.isInteger(ctx.sequenceTotal) ? ctx.sequenceTotal : null,
        productId: ctx.productId || null,
        productName: ctx.productName || null,
        programTitle: ctx.programTitle || ctx.programLabel || null,
        programLabel: ctx.programLabel || ctx.programTitle || null,
        plannedStartAt: ctx.plannedStartAt || null,
        expireAt: ctx.expireAt || null,
        stepName: ctx.stepName || null,
        orderRef: ctx.orderRef || null,
      };
      meta.questionarioKey =
        meta.percorsoId && Number.isInteger(meta.stepIndex)
          ? `${meta.percorsoId}__${meta.stepIndex}`
          : meta.percorsoId && meta.stepKey
          ? `${meta.percorsoId}__${meta.stepKey}`
          : null;
      return meta;
    };

    const toIsoString = (value) => {
      const date = toDateSafe(value);
      return date ? date.toISOString() : null;
    };

    const syncQuestionarioStepMetadata = async (docId, overrides = {}) => {
      const ctx = percorsoContext.value;
      if (!docId || !ctx.percorsoId || ctx.stepIndex == null || ctx.stepIndex < 0) return;
      try {
        const percorsoRef = doc(db, 'percorsi', ctx.percorsoId);
        const snap = await getDoc(percorsoRef);
        if (!snap.exists()) return;
        const data = snap.data() || {};
        const steps = Array.isArray(data.steps) ? [...data.steps] : [];
        const idx = ctx.stepIndex;
        const prev = steps[idx] || {};
        const prevQuestionnaire = prev.questionnaire || {};
        const baseMeta = buildQuestionarioMetadata();
        const mergedMeta = {
          ...prevQuestionnaire,
          ...baseMeta,
          ...overrides,
        };

        mergedMeta.id = docId;
        mergedMeta.type = QUESTIONARIO_TYPE;
        mergedMeta.questionarioUid = docId;
        mergedMeta.percorsoId = mergedMeta.percorsoId || ctx.percorsoId;
        mergedMeta.stepIndex = Number.isInteger(mergedMeta.stepIndex)
          ? mergedMeta.stepIndex
          : Number.isInteger(ctx.stepIndex)
          ? ctx.stepIndex
          : null;
        mergedMeta.stepKey = mergedMeta.stepKey || ctx.stepKey || null;
        mergedMeta.sequenceIndex =
          Number.isInteger(mergedMeta.sequenceIndex) ? mergedMeta.sequenceIndex : Number.isInteger(ctx.sequenceIndex) ? ctx.sequenceIndex : null;
        mergedMeta.sequenceTotal =
          Number.isInteger(mergedMeta.sequenceTotal) ? mergedMeta.sequenceTotal : Number.isInteger(ctx.sequenceTotal) ? ctx.sequenceTotal : null;
        mergedMeta.sequenceGroupId = mergedMeta.sequenceGroupId || ctx.sequenceGroupId || null;
        mergedMeta.productId = mergedMeta.productId || ctx.productId || null;
        mergedMeta.productName = mergedMeta.productName || ctx.productName || null;
        mergedMeta.programTitle = mergedMeta.programTitle || ctx.programTitle || ctx.programLabel || null;
        mergedMeta.programLabel = mergedMeta.programLabel || ctx.programLabel || ctx.programTitle || null;
        mergedMeta.plannedStartAt = mergedMeta.plannedStartAt || ctx.plannedStartAt || null;
        mergedMeta.expireAt = mergedMeta.expireAt || ctx.expireAt || null;
        mergedMeta.stepName = mergedMeta.stepName || ctx.stepName || null;
        mergedMeta.orderRef = mergedMeta.orderRef || ctx.orderRef || null;

        if (!mergedMeta.questionarioKey) {
          const qKey =
            mergedMeta.percorsoId && Number.isInteger(mergedMeta.stepIndex)
              ? `${mergedMeta.percorsoId}__${mergedMeta.stepIndex}`
              : mergedMeta.percorsoId && mergedMeta.stepKey
              ? `${mergedMeta.percorsoId}__${mergedMeta.stepKey}`
              : null;
          mergedMeta.questionarioKey = qKey;
        }

        if (overrides.updatedAt) {
          mergedMeta.updatedAt = toIsoString(overrides.updatedAt);
        } else if (!mergedMeta.updatedAt) {
          mergedMeta.updatedAt = toIsoString(new Date());
        }

        if (overrides.createdAt) {
          mergedMeta.createdAt = toIsoString(overrides.createdAt);
        } else if (!mergedMeta.createdAt && questionarioInfo.value.createdAt) {
          mergedMeta.createdAt = toIsoString(questionarioInfo.value.createdAt);
        }

        mergedMeta.templateId =
          overrides.templateId ??
          mergedMeta.templateId ??
          QUESTIONARIO_TEMPLATE.id;
        mergedMeta.templateKey =
          overrides.templateKey ??
          mergedMeta.templateKey ??
          QUESTIONARIO_TEMPLATE.key;
        mergedMeta.templateLabel =
          overrides.templateLabel ??
          mergedMeta.templateLabel ??
          QUESTIONARIO_TEMPLATE.label;
        mergedMeta.templateVersion =
          overrides.templateVersion ??
          mergedMeta.templateVersion ??
          QUESTIONARIO_TEMPLATE.version;

        const healthMeta = buildHealthSnapshot();
        mergedMeta.patologieMetaboliche = healthMeta.patologieMetaboliche;
        mergedMeta.patologieMuscolo = healthMeta.patologieMuscolo;
        mergedMeta.hasPatologieMetaboliche = healthMeta.hasPatologieMetaboliche;
        mergedMeta.hasPatologieMuscolo = healthMeta.hasPatologieMuscolo;

        steps[idx] = {
          ...prev,
          questionarioDocId: docId,
          questionnaire: mergedMeta,
        };

        await updateDoc(percorsoRef, { steps });
      } catch (error) {
        console.error('Errore aggiornando metadati questionario nel percorso:', error);
      }
    };

    const maybeBackfillQuestionarioDoc = async (docId, data = {}) => {
      if (!docId) return;
      const metadata = buildQuestionarioMetadata();
      const patch = {};
      let needsPatch = false;

      const healthSnapshot = buildHealthSnapshot();
      const combinedMeta = { ...metadata, ...healthSnapshot };

      Object.entries(combinedMeta).forEach(([key, value]) => {
        if (value == null) return;
        const existing = data[key];
        if (existing == null || (typeof existing === 'string' && !existing.trim())) {
          patch[key] = value;
          needsPatch = true;
        }
      });

      if (!data.questionarioUid) {
        patch.questionarioUid = docId;
        needsPatch = true;
      }
      if (metadata.questionarioKey && !data.questionarioKey) {
        patch.questionarioKey = metadata.questionarioKey;
        needsPatch = true;
      }

      if (needsPatch) {
        try {
          await updateDoc(doc(db, QUESTIONARI_COLLECTION, docId), patch);
        } catch (err) {
          console.warn('Impossibile aggiornare i metadati del questionario:', err);
        }
      }
    };

    const hydrateQuestionarioDoc = async (docSnap, snapshotData = null) => {
      if (!docSnap) return false;
      if (typeof docSnap.exists === 'function' && !docSnap.exists()) {
        return false;
      }
      const data =
        snapshotData ??
        (typeof docSnap.data === 'function' ? docSnap.data() || {} : {});

      questionarioDocId.value = docSnap.id;
      questionarioInfo.value = {
        createdAt: toDateSafe(data.createdAt) || null,
        updatedAt: toDateSafe(data.updatedAt || data.createdAt) || null,
        templateLabel: data.templateLabel || QUESTIONARIO_TEMPLATE.label,
      };
      questionarioPartial.value = !!data.allowPartial;
      setFormData(data.answers || {}, data);
      await maybeBackfillQuestionarioDoc(docSnap.id, data);
      await syncQuestionarioStepMetadata(docSnap.id, {
        templateId: data.templateId || QUESTIONARIO_TEMPLATE.id,
        templateKey: data.templateKey || data.questionarioTemplateKey || QUESTIONARIO_TEMPLATE.key,
        templateLabel: data.templateLabel || QUESTIONARIO_TEMPLATE.label,
        templateVersion: data.templateVersion ?? QUESTIONARIO_TEMPLATE.version,
        createdAt: toDateSafe(data.createdAt) || undefined,
        updatedAt: toDateSafe(data.updatedAt || data.createdAt) || undefined,
        questionarioKey: data.questionarioKey,
        questionarioUid: data.questionarioUid || docSnap.id,
      });
      evaluateManualDocLinkState(docSnap.id, data);
      return true;
    };

    const buildQuestionarioUpdatePayload = (extra = {}) => {
      const base = {
        ...buildQuestionarioMetadata(),
        questionarioUid: questionarioDocId.value || null,
        ...buildHealthSnapshot(),
        ...extra,
      };
      if (!('updatedAt' in base)) {
        base.updatedAt = serverTimestamp();
      }
      return base;
    };

    const normalizeYesNo = (value) => {
      const str = String(value ?? '').trim().toLowerCase();
      if (!str) return '';
      if (['si', 'sì', 'yes', 'true', '1'].includes(str)) return 'Si';
      if (['no', 'false', '0'].includes(str)) return 'No';
      return '';
    };

    const buildHealthSnapshot = () => {
      const metab = normalizeYesNo(form.haPatologieMetaboliche) || 'No';
      const muscolo = normalizeYesNo(form.haPatologieMuscolo) || 'No';
      return {
        patologieMetaboliche: metab,
        patologieMuscolo: muscolo,
        hasPatologieMetaboliche: metab === 'Si',
        hasPatologieMuscolo: muscolo === 'Si',
      };
    };

    const syncBirthdatePartsFromValue = (value) => {
      if (!value) {
        birthdateParts.day = '';
        birthdateParts.month = '';
        birthdateParts.year = '';
        return;
      }
      const parsed = new Date(value);
      if (Number.isNaN(parsed.getTime())) {
        birthdateParts.day = '';
        birthdateParts.month = '';
        birthdateParts.year = '';
        return;
      }
      birthdateParts.year = String(parsed.getUTCFullYear());
      birthdateParts.month = String(parsed.getUTCMonth() + 1).padStart(2, '0');
      birthdateParts.day = String(parsed.getUTCDate()).padStart(2, '0');
    };

    const updateBirthdateFromParts = () => {
      if (birthdateParts.year && birthdateParts.month && birthdateParts.day) {
        form.dataNascita = `${birthdateParts.year}-${birthdateParts.month}-${birthdateParts.day}`;
      } else {
        form.dataNascita = '';
      }
    };

    const updateFuscoNameFromProfile = () => {
      const fullName = [userProfile.value.name, userProfile.value.surname].filter(Boolean).join(' ').trim();
      if (!fullName) return;
      if (form.fuscoNomeCognome && form.fuscoNomeCognome.trim()) return;
      suppressDirty.value = true;
      form.fuscoNomeCognome = fullName;
      suppressDirty.value = false;
    };

    const fetchUserProfile = async () => {
      const uid = effectiveUserId.value;
      if (!uid) return;
      try {
        const snap = await getDoc(doc(db, 'users', uid));
        if (!snap.exists()) return;
        const data = snap.data() || {};
        userProfile.value = {
          name: toTrimmedStringOrNull(data.name) || '',
          surname: toTrimmedStringOrNull(data.surname) || '',
        };
        updateFuscoNameFromProfile();
      } catch (err) {
        console.error('Errore recuperando il profilo utente:', err);
      }
    };

    const setFormData = (data, fullDoc = null) => {
      const base = defaultForm();
      assignDeep(base, data || {});
      const legacyProfile = data?.foto?.profilo;
      if (legacyProfile) {
        if (!base.foto.profiloDestro) base.foto.profiloDestro = legacyProfile;
        if (!base.foto.profiloSinistro) base.foto.profiloSinistro = legacyProfile;
      }

      suppressDirty.value = true;
      assignDeep(form, base);
      lastLoaded.value = deepClone(base);

      syncBirthdatePartsFromValue(form.dataNascita);

      PATHOLOGIES.forEach((item) =>
        handleDiagnosiChange('patologie', item.key, form.patologie[item.key].diagnosi),
      );
      form.patologieAltre.forEach((entry, index) =>
        handleDiagnosiChange('patologieAltre', index, entry.diagnosi),
      );
      MUSCLE_PATHOLOGIES.forEach((item) =>
        handleDiagnosiChange('patologieMuscolo', item.key, form.patologieMuscolo[item.key].diagnosi),
      );
      form.patologieMuscoloAltre.forEach((entry, index) =>
        handleDiagnosiChange('patologieMuscoloAltre', index, entry.diagnosi),
      );

      if (fullDoc) {
        const metab = normalizeYesNo(fullDoc.patologieMetaboliche);
        if (metab) form.haPatologieMetaboliche = metab;
        const muscolo = normalizeYesNo(fullDoc.patologieMuscolo);
        if (muscolo) form.haPatologieMuscolo = muscolo;
      }

      const hasMetabolicData =
        (data &&
          PATHOLOGIES.some((item) => data.patologie?.[item.key]?.presente || data.patologie?.[item.key]?.diagnosi)) ||
        (data && Array.isArray(data.patologieAltre) && data.patologieAltre.length > 0);
      const derivedMetab = hasMetabolicData ? 'Si' : 'No';
      if (!normalizeYesNo(form.haPatologieMetaboliche)) {
        form.haPatologieMetaboliche = derivedMetab;
      }

      const hasMuscoloData =
        (data &&
          MUSCLE_PATHOLOGIES.some(
            (item) => data.patologieMuscolo?.[item.key]?.presente || data.patologieMuscolo?.[item.key]?.diagnosi,
          )) ||
        (data && Array.isArray(data.patologieMuscoloAltre) && data.patologieMuscoloAltre.length > 0) ||
        (data && Array.isArray(data.infortuni) && data.infortuni.length > 0) ||
        (data && Array.isArray(data.interventi) && data.interventi.length > 0) ||
        (data && (data.noteLimitazioni || data.farmaciAssunti));
      const derivedMuscolo = hasMuscoloData ? 'Si' : 'No';
      if (!normalizeYesNo(form.haPatologieMuscolo)) {
        form.haPatologieMuscolo = derivedMuscolo;
      }

      if (!form.idoneita.autocertificazioneTesto) {
        form.idoneita.autocertificazioneTesto = AUTOCERTIFICAZIONE_TEMPLATE;
      }

      updateFuscoNameFromProfile();

      dirty.value = false;
      nextTick(() => {
        suppressDirty.value = false;
      });
    };

    const detachManualQuestionarioDoc = async () => {
      if (!manualDocLinkState.shouldDetach || !questionarioDocId.value) return questionarioDocId.value;
      const ctx = percorsoContext.value;
      if (!ctx.percorsoId || !Number.isInteger(ctx.stepIndex)) {
        resetManualDocLinkState();
        return questionarioDocId.value;
      }
      const snapshotData = manualDocLinkState.snapshot || {};
      const newDocRef = doc(collection(db, QUESTIONARI_COLLECTION));
      const metadata = buildQuestionarioMetadata();
      const templateInfo = {
        templateId: snapshotData.templateId || QUESTIONARIO_TEMPLATE.id,
        templateKey: snapshotData.templateKey || QUESTIONARIO_TEMPLATE.key,
        templateLabel: snapshotData.templateLabel || QUESTIONARIO_TEMPLATE.label,
        templateVersion: snapshotData.templateVersion ?? QUESTIONARIO_TEMPLATE.version,
      };
      const createdAtValue =
        snapshotData.createdAt || questionarioInfo.value.createdAt || new Date();
      const updatedAtValue = new Date();
      const payload = {
        ...snapshotData,
        ...metadata,
        ...buildHealthSnapshot(),
        ...templateInfo,
        uid: effectiveUserId.value,
        answers: plainAnswers(),
        allowPartial: snapshotData.allowPartial ?? questionarioPartial.value,
        manualStandalone: false,
        questionarioUid: newDocRef.id,
        questionarioKey: metadata.questionarioKey || snapshotData.questionarioKey || null,
        createdAt: createdAtValue,
        updatedAt: updatedAtValue,
      };
      payload.type = snapshotData.type || QUESTIONARIO_TYPE;
      delete payload.id;
      await setDoc(newDocRef, payload);

      questionarioDocId.value = newDocRef.id;
      questionarioPartial.value = !!payload.allowPartial;
      questionarioInfo.value = {
        createdAt: toDateSafe(createdAtValue) || null,
        updatedAt: updatedAtValue,
        templateLabel: payload.templateLabel || QUESTIONARIO_TEMPLATE.label,
      };
      resetManualDocLinkState();

      try {
        await router.replace({
          name: route.name || 'UserProgrammiQuestionario',
          params: route.params,
          query: { ...route.query, questionarioDocId: newDocRef.id },
        });
      } catch (navError) {
        console.warn('Impossibile aggiornare l\'URL del questionario duplicato:', navError);
      }

      await syncQuestionarioStepMetadata(newDocRef.id, {
        ...templateInfo,
        createdAt: toDateSafe(createdAtValue) || undefined,
        updatedAt: updatedAtValue,
        questionarioKey: metadata.questionarioKey,
        questionarioUid: newDocRef.id,
      });

      return newDocRef.id;
    };

    const normalizeStepKey = (value) => String(value || '').trim().toLowerCase() || 'questionario';

    const findReusableManualDocId = async () => {
      const uid = effectiveUserId.value;
      if (!uid) return null;
      const stepKey = normalizeStepKey(percorsoContext.value.stepKey || 'questionario');
      try {
        const snap = await getDocs(query(collection(db, QUESTIONARI_COLLECTION), where('uid', '==', uid)));
        const candidates = [];
        snap.forEach((docSnap) => {
          const data = docSnap.data() || {};
          if (data.percorsoId) return;
          const key = normalizeStepKey(data.stepKey || data.questionarioStepKey || '');
          if (key && key !== stepKey) return;
          const looksManual =
            data.manualStandalone === true ||
            (!data.percorsoId && !data.sequenceGroupId && !data.sequenceIndex && !data.sequenceTotal);
          if (!looksManual) return;
          const ts = toDateSafe(data.updatedAt || data.createdAt) || new Date(0);
          candidates.push({ id: docSnap.id, ts });
        });
        if (!candidates.length) return null;
        candidates.sort((a, b) => b.ts.getTime() - a.ts.getTime());
        return candidates[0].id;
      } catch (err) {
        console.warn('findReusableManualDocId error', err);
        return null;
      }
    };

    const ensureAuthForLink = async () => {
      if (auth.currentUser) {
        if (!currentUserId.value) currentUserId.value = auth.currentUser.uid;
        return;
      }
      const token = customToken.value;
      if (!token) return;
      try {
        await signInWithCustomToken(auth, token);
        if (!currentUserId.value && auth.currentUser?.uid) {
          currentUserId.value = auth.currentUser.uid;
        }
      } catch (err) {
        console.error('signInWithCustomToken error', err);
        errorMessage.value = 'Accesso non riuscito. Accedi dal link o richiedi un nuovo accesso.';
      }
    };

    const ensureQuestionarioDoc = async () => {
      if (questionarioDocId.value && manualDocLinkState.shouldDetach) {
        await detachManualQuestionarioDoc();
      }
      if (!questionarioDocId.value && manualStandaloneView.value) {
        const reusableId = await findReusableManualDocId();
          if (reusableId) {
            questionarioDocId.value = reusableId;
            try {
              const nextQuery = { ...route.query, questionarioDocId: reusableId };
              router.replace({ query: nextQuery }).catch(() => {});
            } catch (navErr) {
              console.warn('Impossibile aggiornare la query con il questionario riutilizzato', navErr);
            }
            return reusableId;
          }
        }
      if (questionarioDocId.value) return questionarioDocId.value;
      const uid = effectiveUserId.value || toTrimmedStringOrNull(route.query?.userId);
      if (!uid) throw new Error('ID utente non disponibile.');
      const docRef = doc(collection(db, QUESTIONARI_COLLECTION));
      const metadata = buildQuestionarioMetadata();
      await setDoc(docRef, {
        uid,
        type: QUESTIONARIO_TYPE,
        templateId: QUESTIONARIO_TEMPLATE.id,
        templateKey: QUESTIONARIO_TEMPLATE.key,
        templateLabel: QUESTIONARIO_TEMPLATE.label,
        templateVersion: QUESTIONARIO_TEMPLATE.version,
        answers: plainAnswers(),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        ...metadata,
        questionarioUid: docRef.id,
        ...buildHealthSnapshot(),
      });
      const now = new Date();
      questionarioDocId.value = docRef.id;
      questionarioInfo.value = { createdAt: now, updatedAt: now, templateLabel: QUESTIONARIO_TEMPLATE.label };
      if ((preferredDocId.value || '') !== docRef.id) {
        const nextQuery = { ...route.query, questionarioDocId: docRef.id };
        router.replace({ query: nextQuery }).catch(() => {});
      }
      await syncQuestionarioStepMetadata(docRef.id, {
        ...metadata,
        templateId: QUESTIONARIO_TEMPLATE.id,
        templateKey: QUESTIONARIO_TEMPLATE.key,
        templateLabel: QUESTIONARIO_TEMPLATE.label,
        templateVersion: QUESTIONARIO_TEMPLATE.version,
        createdAt: now,
        updatedAt: now,
      });
      return docRef.id;
    };

    const loadQuestionario = async () => {
      const uid = effectiveUserId.value;
      loading.value = true;
      errorMessage.value = '';
      const preferredId = preferredDocId.value;

      if (!uid && preferredId) {
        try {
          const preferredSnap = await getDoc(doc(db, QUESTIONARI_COLLECTION, preferredId));
          const loaded = await hydrateQuestionarioDoc(preferredSnap);
          if (loaded) {
            const data = preferredSnap.data ? preferredSnap.data() : {};
            const fromUid = toTrimmedStringOrNull(data?.uid || data?.userId || null);
            if (fromUid && !currentUserId.value) currentUserId.value = fromUid;
            loading.value = false;
            return;
          }
        } catch (err) {
          console.warn('Impossibile caricare il questionario preferito:', err);
        }
      }

      if (!uid) {
        errorMessage.value = 'ID utente non disponibile.';
        loading.value = false;
        resetManualDocLinkState();
        return;
      }

      try {
        if (preferredId) {
          try {
            const preferredSnap = await getDoc(doc(db, QUESTIONARI_COLLECTION, preferredId));
            if (await hydrateQuestionarioDoc(preferredSnap)) {
              loading.value = false;
              return;
            }
          } catch (err) {
            console.warn('Impossibile caricare il questionario preferito:', err);
          }
        }

        const ctx = percorsoContext.value;
        const candidateMap = new Map();

        if (ctx.percorsoId && ctx.stepKey) {
          const constraints = [
            where('uid', '==', uid),
            where('type', '==', QUESTIONARIO_TYPE),
            where('percorsoId', '==', ctx.percorsoId),
            where('stepKey', '==', ctx.stepKey),
          ];
          if (Number.isInteger(ctx.stepIndex)) {
            constraints.push(where('stepIndex', '==', ctx.stepIndex));
          }
          const targetedSnap = await getDocs(query(collection(db, QUESTIONARI_COLLECTION), ...constraints));
          targetedSnap.forEach((docSnap) => {
            candidateMap.set(docSnap.id, docSnap);
          });
        }

        if (!candidateMap.size) {
          try {
            const orderedSnap = await getDocs(
              query(
                collection(db, QUESTIONARI_COLLECTION),
                where('uid', '==', uid),
                where('type', '==', QUESTIONARIO_TYPE),
                orderBy('updatedAt', 'desc'),
              ),
            );
            orderedSnap.forEach((docSnap) => candidateMap.set(docSnap.id, docSnap));
          } catch (err) {
            if (err?.code === 'failed-precondition') {
              const fallbackSnap = await getDocs(
                query(
                  collection(db, QUESTIONARI_COLLECTION),
                  where('uid', '==', uid),
                  where('type', '==', QUESTIONARIO_TYPE),
                ),
              );
              fallbackSnap.forEach((docSnap) => candidateMap.set(docSnap.id, docSnap));
            } else {
              throw err;
            }
          }
        }

        const candidateDocs = Array.from(candidateMap.values()).map((docSnap) => {
          const data = docSnap.data() || {};
          const updated = toDateSafe(data.updatedAt || data.createdAt);
          return {
            docSnap,
            data,
            sortValue: updated ? updated.getTime() : 0,
          };
        });

        candidateDocs.sort((a, b) => b.sortValue - a.sortValue);

        const ctxForSelection = ctx;
        const wantSpecificStep = !!(ctxForSelection.percorsoId && ctxForSelection.stepKey);
        const wantSpecificIndex = Number.isInteger(ctxForSelection.stepIndex);
        const pickCandidate = () => {
          if (!candidateDocs.length) return null;
          if (!wantSpecificStep) return candidateDocs[0];

          const normalizeStepKey = (value) => String(value || '').toLowerCase();
          const strictMatch = candidateDocs.find(({ data }) => {
            const meta = data || {};
            if (meta.percorsoId && ctxForSelection.percorsoId && meta.percorsoId !== ctxForSelection.percorsoId) return false;
            if (normalizeStepKey(meta.stepKey) !== normalizeStepKey(ctxForSelection.stepKey)) return false;
            if (wantSpecificIndex) {
              const docIndex = toStepIndex(meta.stepIndex);
              return docIndex === ctxForSelection.stepIndex;
            }
            return true;
          });
          if (strictMatch) return strictMatch;

          const relaxedMatch = candidateDocs.find(({ data }) => {
            const meta = data || {};
            if (meta.percorsoId && ctxForSelection.percorsoId && meta.percorsoId !== ctxForSelection.percorsoId) return false;
            return normalizeStepKey(meta.stepKey) === normalizeStepKey(ctxForSelection.stepKey);
          });
          if (relaxedMatch) return relaxedMatch;

          return null;
        };

        const selected = pickCandidate();

        if (selected) {
          await hydrateQuestionarioDoc(selected.docSnap, selected.data);
        } else {
          questionarioDocId.value = '';
          questionarioInfo.value = { createdAt: null, updatedAt: null, templateLabel: QUESTIONARIO_TEMPLATE.label };
          setFormData({}, null);
          questionarioPartial.value = false;
          resetManualDocLinkState();
        }
      } catch (error) {
        console.error('Errore caricando il questionario:', error);
        errorMessage.value = 'Impossibile recuperare il questionario.';
        questionarioDocId.value = '';
        questionarioInfo.value = { createdAt: null, updatedAt: null, templateLabel: QUESTIONARIO_TEMPLATE.label };
        setFormData({});
        questionarioPartial.value = false;
        resetManualDocLinkState();
      } finally {
        loading.value = false;
      }
    };

    const loadWithAuth = async () => {
      await ensureAuthForLink();
      await loadQuestionario();
      await fetchUserProfile();
    };

    const saveQuestionario = async () => {
      if (saving.value || loading.value) return;
      const uid = effectiveUserId.value;
      if (!uid) {
        errorMessage.value = 'ID utente non disponibile.';
        return;
      }

      saving.value = true;
      errorMessage.value = '';

      try {
        const docId = await ensureQuestionarioDoc();
        const docRef = doc(db, QUESTIONARI_COLLECTION, docId);
        await updateDoc(docRef, buildQuestionarioUpdatePayload({
          answers: plainAnswers(),
          templateVersion: QUESTIONARIO_TEMPLATE.version,
          templateLabel: QUESTIONARIO_TEMPLATE.label,
          templateId: QUESTIONARIO_TEMPLATE.id,
          templateKey: QUESTIONARIO_TEMPLATE.key,
          questionarioUid: docId,
        }));

        dirty.value = false;
        lastLoaded.value = deepClone(form);
        const now = new Date();
        questionarioInfo.value = {
          createdAt: questionarioInfo.value.createdAt || now,
          updatedAt: now,
          templateLabel: QUESTIONARIO_TEMPLATE.label,
        };
        await syncQuestionarioStepMetadata(docId, {
          updatedAt: now,
          templateVersion: QUESTIONARIO_TEMPLATE.version,
          templateLabel: QUESTIONARIO_TEMPLATE.label,
          templateId: QUESTIONARIO_TEMPLATE.id,
          templateKey: QUESTIONARIO_TEMPLATE.key,
        });
        showActionMessage(
          'Il questionario è stato salvato correttamente, ricorda di inviarlo per inserirlo nei questionari da lavorare',
          'success'
        );
      } catch (error) {
        console.error('Errore nel salvataggio del questionario:', error);
        errorMessage.value = 'Errore durante il salvataggio del questionario.';
      } finally {
        saving.value = false;
      }
    };

    const resetForm = () => {
      setFormData(lastLoaded.value);
    };

    const completeQuestionarioStep = async () => {
      if (completingStep.value || saving.value || loading.value) return;
      const ctx = percorsoContext.value;
      if (!ctx.percorsoId || !Number.isInteger(ctx.stepIndex)) {
        showActionMessage('Essendo un questionario manuela, deve essere collegato ad un ordine/percorso.', 'error');
        return;
      }
      try {
        completingStep.value = true;
        if (dirty.value || !questionarioDocId.value || manualDocLinkState.shouldDetach) {
          await saveQuestionario();
        }
        if (!questionarioDocId.value) {
          showActionMessage('Salva il questionario prima di inviarlo.', 'error');
          return;
        }
        await setStepDone(ctx.percorsoId, ctx.stepIndex, true);
        showActionMessage('Questionario segnato come inviato, adesso lo troverai in questionari da lavorare');
      } catch (error) {
        console.error('completeQuestionarioStep error:', error);
        showActionMessage('Impossibile segnare il questionario come inviato.', 'error');
      } finally {
        completingStep.value = false;
      }
    };

    const uploadSingleFile = async (segments, file) => {
      const uid = effectiveUserId.value;
      if (!uid) {
        errorMessage.value = 'ID utente non disponibile.';
        showActionMessage('Sessione scaduta: effettua nuovamente il login.', 'error');
        throw new Error('ID utente non disponibile.');
      }
      const docId = await ensureQuestionarioDoc();
      const fileName = `${Date.now()}_${sanitizeName(file.name)}`;
      const path = makeStoragePath(uid, docId, ...segments, fileName);
      const ref = storageRef(storage, path);
      await uploadBytesResumable(ref, file);
      const url = await getDownloadURL(ref);
      return { name: file.name, size: file.size, contentType: file.type || null, path, url };
    };

    const onPhotoChange = async (fieldKey, event) => {
      const file = event.target.files?.[0];
      event.target.value = '';
      if (!file) return;
      setUploadState(uploadingPhoto, fieldKey, true);
      try {
        const metadata = await uploadSingleFile(['foto', fieldKey], file);
        suppressDirty.value = true;
        form.foto[fieldKey] = metadata;
        suppressDirty.value = false;
        await updateDoc(
          doc(db, QUESTIONARI_COLLECTION, questionarioDocId.value),
          buildQuestionarioUpdatePayload({ answers: plainAnswers() })
        );
        lastLoaded.value = deepClone(form);
        await syncQuestionarioStepMetadata(questionarioDocId.value, { updatedAt: new Date() });
      } catch (error) {
        console.error('Errore caricando la foto:', error);
        if (!errorMessage.value) {
          errorMessage.value = 'Errore durante il caricamento della foto.';
          showActionMessage(errorMessage.value, 'error');
        }
      } finally {
        setUploadState(uploadingPhoto, fieldKey, false);
      }
    };

    const removePhoto = async (fieldKey) => {
      const metadata = form.foto[fieldKey];
      if (!metadata) return;
      if (typeof window !== 'undefined') {
        const confirmDelete = window.confirm(`Eliminare "${metadata.name}"?`);
        if (!confirmDelete) return;
      }
      try {
        await deleteObject(storageRef(storage, metadata.path));
        suppressDirty.value = true;
        form.foto[fieldKey] = null;
        suppressDirty.value = false;
        await updateDoc(
          doc(db, QUESTIONARI_COLLECTION, questionarioDocId.value),
          buildQuestionarioUpdatePayload({ answers: plainAnswers() })
        );
        lastLoaded.value = deepClone(form);
        await syncQuestionarioStepMetadata(questionarioDocId.value, { updatedAt: new Date() });
      } catch (error) {
        console.error('Errore eliminando la foto:', error);
        errorMessage.value = 'Errore durante l\'eliminazione della foto.';
      }
    };

    const onIdoneitaFileChange = async (fieldKey, event) => {
      const file = event.target.files?.[0];
      event.target.value = '';
      if (!file) return;
      setUploadState(uploadingIdoneita, fieldKey, true);
      try {
        const metadata = await uploadSingleFile(['idoneita', fieldKey], file);
        suppressDirty.value = true;
        form.idoneita[fieldKey] = metadata;
        suppressDirty.value = false;
        await updateDoc(
          doc(db, QUESTIONARI_COLLECTION, questionarioDocId.value),
          buildQuestionarioUpdatePayload({ answers: plainAnswers() })
        );
        lastLoaded.value = deepClone(form);
        await syncQuestionarioStepMetadata(questionarioDocId.value, { updatedAt: new Date() });
      } catch (error) {
        console.error('Errore caricando il file:', error);
        if (!errorMessage.value) {
          errorMessage.value = 'Errore durante il caricamento del file.';
          showActionMessage(errorMessage.value, 'error');
        }
      } finally {
        setUploadState(uploadingIdoneita, fieldKey, false);
      }
    };

    const removeIdoneitaFile = async (fieldKey) => {
      const metadata = form.idoneita[fieldKey];
      if (!metadata) return;
      if (typeof window !== 'undefined') {
        const confirmDelete = window.confirm(`Eliminare "${metadata.name}"?`);
        if (!confirmDelete) return;
      }
      try {
        await deleteObject(storageRef(storage, metadata.path));
        suppressDirty.value = true;
        form.idoneita[fieldKey] = null;
        suppressDirty.value = false;
        await updateDoc(
          doc(db, QUESTIONARI_COLLECTION, questionarioDocId.value),
          buildQuestionarioUpdatePayload({ answers: plainAnswers() })
        );
        lastLoaded.value = deepClone(form);
        await syncQuestionarioStepMetadata(questionarioDocId.value, { updatedAt: new Date() });
      } catch (error) {
        console.error('Errore eliminando il file:', error);
        errorMessage.value = 'Errore durante l\'eliminazione del file.';
      }
    };

    const addGravidanza = () => {
      form.gravidanze.push({ anno: '', parto: '' });
    };
    const removeGravidanza = (index) => {
      form.gravidanze.splice(index, 1);
    };

    const addAltraPatologia = () => {
      form.patologieAltre.push({
        nome: '',
        diagnosi: '',
        certificazioneFile: null,
        note: '',
      });
    };
    const removeAltraPatologia = async (index) => {
      const item = form.patologieAltre[index];
      if (item?.certificazioneFile) {
        await deleteStoredFile(item.certificazioneFile);
      }
      form.patologieAltre.splice(index, 1);
    };

    const addProblematicaMuscolo = () => {
      form.patologieMuscoloAltre.push({
        zona: '',
        diagnosi: '',
        certificazioneFile: null,
        note: '',
      });
    };
    const removeProblematicaMuscolo = async (index) => {
      const item = form.patologieMuscoloAltre[index];
      if (item?.certificazioneFile) {
        await deleteStoredFile(item.certificazioneFile);
      }
      form.patologieMuscoloAltre.splice(index, 1);
    };

    const addInfortunio = () => {
      form.infortuni.push({ descrizione: '', anno: '', limitazioni: '' });
    };
    const removeInfortunio = (index) => {
      form.infortuni.splice(index, 1);
    };

    const addIntervento = () => {
      form.interventi.push({ descrizione: '', anno: '', limitazioni: '' });
    };
    const removeIntervento = (index) => {
      form.interventi.splice(index, 1);
    };

    const deleteStoredFile = async (metadata) => {
      if (!metadata?.path) return;
      try {
        await deleteObject(storageRef(storage, metadata.path));
      } catch (error) {
        console.error('Errore eliminando il file di certificazione:', error);
      }
    };

    const getCertificationTarget = (group, identifier) => {
      if (group === 'patologie') return form.patologie[identifier];
      if (group === 'patologieAltre') return form.patologieAltre[identifier];
      if (group === 'patologieMuscolo') return form.patologieMuscolo[identifier];
      if (group === 'patologieMuscoloAltre') return form.patologieMuscoloAltre[identifier];
      return null;
    };

    const clearCertification = (group, identifier, { removeRemote = false, silent = false } = {}) => {
      const target = getCertificationTarget(group, identifier);
      if (!target) return;
      if (removeRemote && target.certificazioneFile) {
        deleteStoredFile(target.certificazioneFile).catch(() => {});
      }
      if (silent) suppressDirty.value = true;
      target.certificazioneFile = null;
      if (silent) suppressDirty.value = false;
    };

    const makeCertificationSegments = (group, identifier) => {
      switch (group) {
        case 'patologie':
          return ['patologie', identifier, 'certificazioni'];
        case 'patologieAltre':
          return ['patologie_altre', String(identifier), 'certificazioni'];
        case 'patologieMuscolo':
          return ['patologie_muscolo', identifier, 'certificazioni'];
        case 'patologieMuscoloAltre':
          return ['patologie_muscolo_altre', String(identifier), 'certificazioni'];
        default:
          return ['certificazioni_generiche'];
      }
    };

    const onCertificationFileChange = async (group, identifier, event) => {
      const file = event.target.files?.[0];
      event.target.value = '';
      if (!file) return;
      errorMessage.value = '';
      const key = certificationUploadKey(group, identifier);
      setUploadState(uploadingCertification, key, true);
      try {
        const metadata = await uploadSingleFile(makeCertificationSegments(group, identifier), file);
        const target = getCertificationTarget(group, identifier);
        if (!target) return;
        suppressDirty.value = true;
        target.certificazioneFile = metadata;
        suppressDirty.value = false;
        const docId = await ensureQuestionarioDoc();
        await updateDoc(
          doc(db, QUESTIONARI_COLLECTION, docId),
          buildQuestionarioUpdatePayload({ answers: plainAnswers() })
        );
        lastLoaded.value = deepClone(form);
        await syncQuestionarioStepMetadata(docId, { updatedAt: new Date() });
      } catch (error) {
        console.error('Errore caricando il file di certificazione:', error);
        if (!errorMessage.value) {
          errorMessage.value = 'Errore durante il caricamento del file di certificazione.';
          showActionMessage(errorMessage.value, 'error');
        }
      } finally {
        setUploadState(uploadingCertification, key, false);
      }
    };

    const removeCertificationFile = async (group, identifier) => {
      const target = getCertificationTarget(group, identifier);
      const metadata = target?.certificazioneFile;
      if (!metadata) return;
      if (typeof window !== 'undefined') {
        const confirmDelete = window.confirm(`Eliminare "${metadata.name}"?`);
        if (!confirmDelete) return;
      }
      errorMessage.value = '';
      try {
        await deleteStoredFile(metadata);
        clearCertification(group, identifier, { silent: true });
        const docId = await ensureQuestionarioDoc();
        await updateDoc(
          doc(db, QUESTIONARI_COLLECTION, docId),
          buildQuestionarioUpdatePayload({ answers: plainAnswers() })
        );
        lastLoaded.value = deepClone(form);
        await syncQuestionarioStepMetadata(docId, { updatedAt: new Date() });
      } catch (error) {
        console.error('Errore eliminando il file di certificazione:', error);
        errorMessage.value = 'Errore durante l\'eliminazione del file di certificazione.';
      }
    };

    const handleDiagnosiChange = (group, identifier, value) => {
      const target = getCertificationTarget(group, identifier);
      if (!target) return;
      if (value === 'Diagnosi certificata') return;
      clearCertification(group, identifier, { removeRemote: true });
    };

    const resetMetabolicBlocks = async ({ removeRemote = false } = {}) => {
      PATHOLOGIES.forEach((item) => {
        const target = form.patologie[item.key];
        target.presente = false;
        target.diagnosi = '';
        target.note = '';
        clearCertification('patologie', item.key, { removeRemote, silent: true });
      });
      if (removeRemote) {
        await Promise.all(
          form.patologieAltre
            .filter((entry) => entry?.certificazioneFile)
            .map((entry) => deleteStoredFile(entry.certificazioneFile)),
        );
      }
      form.patologieAltre.splice(0, form.patologieAltre.length);
      form.dichiarazionePatologie = false;
    };

    const resetMuscoloBlocks = async ({ removeRemote = false } = {}) => {
      MUSCLE_PATHOLOGIES.forEach((item) => {
        const target = form.patologieMuscolo[item.key];
        target.presente = false;
        target.diagnosi = '';
        target.note = '';
        if (item.askCm) target.cm = '';
        clearCertification('patologieMuscolo', item.key, { removeRemote, silent: true });
      });
      if (removeRemote) {
        await Promise.all(
          form.patologieMuscoloAltre
            .filter((entry) => entry?.certificazioneFile)
            .map((entry) => deleteStoredFile(entry.certificazioneFile)),
        );
      }
      form.patologieMuscoloAltre.splice(0, form.patologieMuscoloAltre.length);
      form.infortuni.splice(0, form.infortuni.length);
      form.interventi.splice(0, form.interventi.length);
      form.noteLimitazioni = '';
      form.farmaciAssunti = '';
    };

    watch(
      () => `${percorsoContext.value.percorsoId || 'nop'}__${
        Number.isInteger(percorsoContext.value.stepIndex) ? percorsoContext.value.stepIndex : 'x'
      }`,
      () => {
        refreshAdminState();
      },
      { immediate: true },
    );

    watch(
      () => form.integratoriUsati,
      (value) => {
        if (value !== 'Si') form.integratoriUsatiQuali = '';
      },
    );

    watch(
      () => form.integratoriAttuali,
      (value) => {
        if (value !== 'Si') form.integratoriAttualiQuali = '';
      },
    );

    watch(
      () => form.attrezzaturaCasa.includes('Altro'),
      (hasAltro) => {
        if (!hasAltro) form.attrezzaturaCasaAltro = '';
      },
    );

    watch(
      () => [birthdateParts.day, birthdateParts.month, birthdateParts.year],
      () => {
        updateBirthdateFromParts();
      },
    );

    watch(
      () => form.dataNascita,
      (value) => {
        syncBirthdatePartsFromValue(value);
      },
    );

    PATHOLOGIES.forEach((item) => {
      watch(
        () => form.patologie[item.key].presente,
        (value) => {
          if (!value) {
            form.patologie[item.key].diagnosi = '';
            clearCertification('patologie', item.key, { removeRemote: true });
            form.patologie[item.key].note = '';
          }
        },
      );
      watch(
        () => form.patologie[item.key].diagnosi,
        (value) => {
          handleDiagnosiChange('patologie', item.key, value);
        },
      );
    });

    MUSCLE_PATHOLOGIES.forEach((item) => {
      watch(
        () => form.patologieMuscolo[item.key].presente,
        (value) => {
          if (!value) {
            form.patologieMuscolo[item.key].diagnosi = '';
            clearCertification('patologieMuscolo', item.key, { removeRemote: true });
            form.patologieMuscolo[item.key].note = '';
            if (item.askCm) form.patologieMuscolo[item.key].cm = '';
          }
        },
      );
      watch(
        () => form.patologieMuscolo[item.key].diagnosi,
        (value) => {
          handleDiagnosiChange('patologieMuscolo', item.key, value);
        },
      );
    });

    watch(
      () => form.haPatologieMetaboliche,
      async (value, previous) => {
        if (suppressDirty.value) return;
        if (value === 'Si') return;
        suppressDirty.value = true;
        try {
          await resetMetabolicBlocks({ removeRemote: value === 'No' && previous === 'Si' });
          if (value === '') form.haPatologieMetaboliche = 'No';
        } finally {
          suppressDirty.value = false;
        }
      },
    );

    watch(
      () => form.haPatologieMuscolo,
      async (value, previous) => {
        if (suppressDirty.value) return;
        if (value === 'Si') return;
        suppressDirty.value = true;
        try {
          await resetMuscoloBlocks({ removeRemote: value === 'No' && previous === 'Si' });
          if (value === '') form.haPatologieMuscolo = 'No';
        } finally {
          suppressDirty.value = false;
        }
      },
    );

    watch(
      () => form.allenamentoAttuale,
      (value) => {
        if (value !== 'Si') form.tipiAllenamento = [];
      },
    );

    watch(
      form,
      () => {
        if (!suppressDirty.value) dirty.value = true;
      },
      { deep: true },
    );

    watch(
      questionarioTargetKey,
      (next, prev) => {
        if (prev !== undefined && next !== prev) {
          loadWithAuth();
        }
      },
    );

    watch(
      () => props.userId,
      (next) => {
        if (next && next !== currentUserId.value) {
          currentUserId.value = next;
          loadWithAuth();
        }
      },
    );

    watch(
      () => route.params.userId,
      (next) => {
        if (next && next !== currentUserId.value) {
          currentUserId.value = next;
          loadWithAuth();
        }
      },
    );

    watch(
      () => toTrimmedStringOrNull(route.query?.userId),
      (next) => {
        if (next && next !== currentUserId.value) {
          currentUserId.value = next;
          loadWithAuth();
        }
      },
    );

    watch(
      () => customToken.value,
      (next, prev) => {
        if (next && next !== prev) {
          loadWithAuth();
        }
      },
    );

    onMounted(() => {
      authUnsubscribe = onAuthStateChanged(auth, (user) => {
        if (user && !currentUserId.value) {
          currentUserId.value = user.uid;
        }
      });
      loadWithAuth();
    });

    onBeforeUnmount(() => {
      if (typeof authUnsubscribe === 'function') authUnsubscribe();
      toastTimers.forEach(clearTimeout);
      toastTimers.clear();
    });

    return {
      form,
      loading,
      saving,
      completingStep,
      dirty,
      errorMessage,
      toasts,
      questionarioInfo,
      questionarioPartial,
      partialSaving,
      adminState,
      adminLoading,
      adminSaving,
      adminStatusLabel,
      adminChipClass,
      adminError,
      canManageAdmin,
      needsUpdateOpen,
      needsUpdateNote,
      handleAdminApprove,
      toggleNeedsUpdate,
      saveNeedsUpdateRequest,
      uploadingPhoto,
      uploadingIdoneita,
      isCertificationUploading,
      isImageFile,
      isPdfFile,
      effectiveUserId,
      TURNISTA_OPTIONS,
      WORK_HOURS_OPTIONS,
      LIFESTYLE_OPTIONS,
      SLEEP_HOURS_OPTIONS,
      SLEEP_QUALITY_OPTIONS,
      YES_NO_OPTIONS,
      birthdateParts,
      calculatedAgeLabel,
      DAY_OPTIONS,
      MONTH_OPTIONS,
      YEAR_OPTIONS,
      ALLENAMENTO_ATTUALE_OPTIONS,
      TIPI_ALLENAMENTO,
      ESPERIENZA_SALA_OPTIONS,
      PT_OPTIONS,
      ALVO_OPTIONS,
      CICLO_MESTRUALE_OPTIONS,
      ACQUA_OPTIONS,
      ALCOL_OPTIONS,
      DIAGNOSIS_OPTIONS,
      REGIME_ALIMENTARE_OPTIONS,
      PASTI_OPTIONS,
      SALTARE_PASTI_OPTIONS,
      LUOGO_ALLENAMENTO_OPTIONS,
      FREQUENZA_ALLENAMENTO_OPTIONS,
      DURATA_ALLENAMENTO_OPTIONS,
      ATTREZZATURE,
      AUTOCERTIFICAZIONE_TEMPLATE,
      PATHOLOGIES,
      MUSCLE_PATHOLOGIES,
      formatDate: formatDateUtils,
      saveQuestionario,
      resetForm,
      exportFormAsPdf,
      copyQuestionarioAnswers,
      pasteQuestionarioAnswers,
      printableCard,
      pdfExporting,
      copyingAnswers,
      pastingAnswers,
      completeQuestionarioStep,
      copyFormLink,
      togglePartialQuestionario,
      dismissToast,
      addGravidanza,
      removeGravidanza,
      addAltraPatologia,
      removeAltraPatologia,
      addProblematicaMuscolo,
      removeProblematicaMuscolo,
      addInfortunio,
      removeInfortunio,
      addIntervento,
      removeIntervento,
      onPhotoChange,
      removePhoto,
      onIdoneitaFileChange,
      removeIdoneitaFile,
      onCertificationFileChange,
      removeCertificationFile,
      handleDiagnosiChange,
    };
  },
};
</script>

<style scoped>

.textarea {
  max-width: 90%;

}

.birthdate-inline .birthdate-controls--inline {
  display: flex;
  gap: 8px;
  max-width: 100%;
}

.birthdate-controls--inline select {
  min-width: 86px;
}
.dashboard-layout {
  display: flex;
  justify-content: center;
  align-items: flex-start;
  min-height: 100vh;
  background: linear-gradient(180deg, #eef1ff 0%, #f7f8ff 100%);
}

.content-container {
  background: linear-gradient(180deg, rgba(243, 245, 255, 0.98) 0%, #eef1ff 100%);
  padding-right: 42px;
  padding-bottom: 64px;
}

.questionario-content {
  width: min(1140px, 100%);
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 32px;
}

.questionario-card {
  width: 97%;
  display: flex;
  flex-direction: column;
  gap: 32px;
  background: #ffffff;
  border-radius: 28px;
  border: 1px solid rgba(75, 98, 192, 0.14);
  box-shadow: 0 36px 96px -64px rgba(18, 26, 70, 0.5);
  padding: 36px clamp(28px, 5vw, 48px);
  text-align: left;
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: min(4vw, 26px);
  height: 37px;
}

.card-header__title {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-width: min(72%, 640px);
}

.card-header h1 {
  margin: 0;
  font-size: clamp(1.8rem, 2.2vw, 2.2rem);
  font-weight: 700;
  color: #eeeeee;
  letter-spacing: -0.01em;
}

.muted {
  color: #48527a;
  font-size: 0.95rem;
}

.meta {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 0.9rem;
  color: #2c2c2c;
  background: rgba(200, 200, 200, 0.944);
  border-radius: 999px;
  padding: 6px 14px;
}

.card-header__right {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.card-header__actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.ghost-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 6px 14px;
  border: 1px solid rgba(255, 255, 255, 0.22);
  border-radius: 8px;
  background: transparent;
  color: #f0f0f0;
  font-size: 0.9rem;
  transition: background 0.2s ease, color 0.2s ease;
}

.ghost-button:hover {
  background: rgba(255, 255, 255, 0.12);
  color: #ffffff;
}

.ghost-button__spinner {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.35);
  border-top-color: #ffffff;
  animation: ghost-button-spin 0.9s linear infinite;
}

@keyframes ghost-button-spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.admin-wrapper {
  margin-bottom: 18px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.admin-controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  padding: 10px 14px;
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 12px;
  background: #f8fafc;
}

.admin-controls__status {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.admin-chip {
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 0.85rem;
  font-weight: 600;
  background: #e2e8f0;
  color: #0f172a;
}

.chip-green {
  background: #dcfce7;
  color: #14532d;
}

.chip-orange {
  background: #fef3c7;
  color: #92400e;
}

.chip-neutral {
  background: #e2e8f0;
  color: #334155;
}

.admin-status-note {
  font-size: 0.82rem;
  color: #475569;
}

.admin-controls__actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.admin-btn {
  border: none;
  border-radius: 8px;
  padding: 6px 12px;
  font-size: 0.85rem;
  cursor: pointer;
  transition: opacity 0.2s ease;
}

.admin-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.admin-btn--green {
  background: #16a34a;
  color: #ffffff;
}

.admin-btn--orange {
  background: #f97316;
  color: #ffffff;
}

.admin-btn--blue {
  background: #2563eb;
  color: #ffffff;
}

.admin-btn--grey {
  background: #e2e8f0;
  color: #1f2937;
}

.admin-needs-box {
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 12px;
  padding: 12px 14px;
  background: #ffffff;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.admin-note-input {
  width: 100%;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid #cbd5f5;
  font-size: 0.9rem;
}

.admin-note {
  margin: 0;
  font-size: 0.88rem;
  color: #475569;
}

.admin-helper {
  margin: 12px 0 0;
  font-size: 0.9rem;
}

.admin-error {
  margin: 8px 0 0;
  font-size: 0.85rem;
  color: #b91c1c;
}

.diario-hint {
  margin: 6px 0 4px;
  font-size: 0.95rem;
  color: #2b3a69;
  background: rgba(67, 84, 158, 0.08);
  border-radius: 10px;
  padding: 10px 14px;
}

.diario-label {
  width: 100%;
}

.diario-textarea {
  width: 93%;
  min-height: 90px;
  resize: vertical;
}

.state {
  padding: 12px 16px;
  border-radius: 14px;
  font-size: 0.95rem;
  font-weight: 600;
}

.state-loading {
  background: rgba(76, 120, 255, 0.12);
  color: #2b5de0;
}

.state-error {
  background: rgba(237, 93, 93, 0.12);
  color: #c02134;
}

.state-success {
  background: rgba(53, 214, 126, 0.14);
  color: #1d7a4c;
}

.qi-form {
  display: flex;
  flex-direction: column;
  gap: 32px;
}

.qi-block {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 23px clamp(22px, 4vw, 36px);
  border-radius: 22px;
  background: linear-gradient(180deg, rgba(250, 251, 255, 0.98) 0%, #ffffff 100%);
  border: 1px solid rgba(79, 99, 191, 0.18);
  box-shadow: inset 0 0 0 1px rgba(102, 121, 210, 0.06);
  margin-top: 13px;
}

.qi-block--fusco {
  border-color: #fb923c;
  box-shadow: inset 0 0 0 1px rgba(251, 146, 60, 0.2);
  background: linear-gradient(180deg, rgba(255, 248, 235, 0.98) 0%, #fffaf3 100%);
}

.qi-block__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.qi-block__header-text {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-width: min(75%, 620px);
}

.qi-block__title {
  margin: 0;
  font-size: clamp(1.28rem, 2vw, 1.4rem);
  font-weight: 600;
  color: #16285d;
  letter-spacing: -0.01em;
}

.qi-block__subtitle {
  margin: 0;
  font-size: 0.94rem;
  line-height: 1.5;
}

.qi-block__body {
  display: flex;
  flex-direction: column;
  gap: 22px;
}

.grid {
  display: grid;
  gap: 18px;
}

.grid.two {
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
}

.grid.three {
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
}

label {
  display: flex;
  flex-direction: column;
  gap: 8px;
  font-weight: 500;
  color: #15204a;
}

label input,
label select,
label textarea {
  border: 1.4px solid #cfd4ff;
  border-radius: 14px;
  padding: 12px 14px;
  font-size: 1rem;
  color: #15204a;
  background: #ffffff;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.12s ease;
}

label input:focus,
label select:focus,
label textarea:focus {
  outline: none;
  border-color: #4c6cff;
  box-shadow: 0 0 0 3px rgba(76, 108, 255, 0.2);
  transform: translateY(-1px);
}

label textarea {
  resize: vertical;
  max-height: 80px;
}

.fusco-age-input[readonly] {
  background: #fff7ed;
  border-color: #fdba74;
  color: #9a3412;
}


.checkbox-group,
.equipment-group {
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 18px;
  border-radius: 18px;
  background: linear-gradient(145deg, rgba(76, 108, 255, 0.08), rgba(76, 108, 255, 0.02));
  border: 1px solid rgba(76, 108, 255, 0.12);
}

.checkbox-item {
  display: grid;
  grid-template-columns: auto 1fr;
  align-items: center;
  column-gap: 12px;
  padding: 4px 0;
  color: #18254f;
  cursor: pointer;
}

.checkbox-item span {
  line-height: 1.45;
}

.checkbox-item input[type='checkbox'] {
  appearance: none;
  width: 18px;
  height: 18px;
  border: 2px solid rgba(76, 108, 255, 0.4);
  border-radius: 6px;
  background: #ffffff;
  display: grid;
  place-items: center;
  margin: 0;
  cursor: pointer;
  transition: border-color 0.2s ease, background 0.2s ease, box-shadow 0.2s ease;
}

.checkbox-item input[type='checkbox']::after {
  content: '';
  width: 9px;
  height: 9px;
  border-radius: 3px;
  background: linear-gradient(135deg, #4663ff, #354de3);
  transform: scale(0);
  transition: transform 0.2s ease;
}

.checkbox-item input[type='checkbox']:checked {
  border-color: rgba(70, 97, 238, 0.9);
  background: rgba(70, 97, 238, 0.08);
  box-shadow: 0 0 0 3px rgba(70, 97, 238, 0.12);
}

.checkbox-item input[type='checkbox']:checked::after {
  transform: scale(1);
}

.checkbox-item input[type='checkbox']:focus-visible {
  outline: none;
  box-shadow: 0 0 0 4px rgba(70, 97, 238, 0.25);
}

.group-label {
  font-weight: 600;
  letter-spacing: 0.01em;
  color: #16285d;
}

.equipment-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 14px;
  justify-items: start;
}

.equipment-group,
.equipment-group span,
.certification-panel span,
.certification-panel label span {
  color: #16285d;
}

.equipment-pill {
  position: relative;
  display: inline-flex;
  align-items: center;
  padding: 12px 18px 12px 46px;
  border: 1px solid rgba(76, 108, 255, 0.25);
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.95);
  cursor: pointer;
  transition: border-color 0.2s ease, background 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;
}

.equipment-pill:hover {
  border-color: rgba(76, 108, 255, 0.6);
  background: rgba(255, 255, 255, 1);
  box-shadow: 0 8px 18px rgba(76, 108, 255, 0.12);
  transform: translateY(-1px);
}

.equipment-pill input[type='checkbox'] {
  position: absolute;
  inset: 0;
  opacity: 0;
  cursor: pointer;
}

.equipment-pill input[type='checkbox']:focus-visible + .equipment-label::before {
  box-shadow: 0 0 0 4px rgba(70, 97, 238, 0.25);
}

.equipment-label {
  position: relative;
  font-weight: 600;
  color: #16285d;
  letter-spacing: 0.01em;
}

.equipment-label::before,
.equipment-label::after {
  content: '';
  position: absolute;
  left: -28px;
  top: 50%;
  transform: translateY(-50%);
  transition: all 0.2s ease;
}

.equipment-label::before {
  width: 18px;
  height: 18px;
  border-radius: 6px;
  border: 2px solid rgba(76, 108, 255, 0.45);
  background: #ffffff;
}

.equipment-label::after {
  width: 8px;
  height: 8px;
  border-radius: 3px;
  background: linear-gradient(135deg, #4663ff, #354de3);
  transform: translateY(-50%) scale(0);
  left: -19px;
}

.equipment-pill input[type='checkbox']:checked + .equipment-label::before {
  border-color: rgba(70, 97, 238, 0.9);
  background: rgba(70, 97, 238, 0.1);
  box-shadow: 0 0 0 2px rgba(70, 97, 238, 0.15);
}

.equipment-pill input[type='checkbox']:checked + .equipment-label::after {
  transform: translateY(-50%) scale(1);
}

@media (max-width: 480px) {
  .equipment-group {
    padding: 16px;
  }
  .equipment-pill {
    padding: 10px 16px 10px 42px;
  }
  .equipment-label::before {
    left: -26px;
  }
  .equipment-label::after {
    left: -17px;
  }
}

.repeater {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.qi-subheader {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.qi-subheader__title {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 600;
  color: #16285d;
}

.repeater-item {
  display: grid;
  gap: 14px;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  padding: 18px;
  border: 1px dashed rgba(76, 108, 255, 0.45);
  border-radius: 16px;
  background: #ffffff;
}

.link {
  background: transparent;
  border: none;
  color: #3452e5;
  font-weight: 600;
  cursor: pointer;
  padding: 0;
  transition: color 0.15s ease;
}

.link:hover {
  color: #2537a8;
}

.link.danger {
  color: #d3474c;
}

.pathology-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(230px, 1fr));
  gap: 16px;
}

.pathology-card {
  border: 1px solid rgba(113, 133, 222, 0.2);
  border-radius: 18px;
  padding: 18px;
  background: rgba(247, 249, 255, 0.85);
  display: flex;
  flex-direction: column;
  gap: 12px;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.pathology-card:hover {
  border-color: rgba(76, 108, 255, 0.48);
  box-shadow: 0 16px 32px -28px rgba(18, 26, 70, 0.5);
}

.pathology-body {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-top: 10px;
  border-top: 1px solid rgba(76, 108, 255, 0.12);
}

.upload-card {
  border: 1px dashed rgba(76, 108, 255, 0.4);
  border-radius: 18px;
  padding: 18px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  background: rgba(248, 250, 255, 0.95);
}

.upload-card input[type='file'] {
  border: none;
  padding: 0;
}

.photo-intro {
  margin: 0;
  font-size: 0.95rem;
  color: #121d3a;
  font-weight: 500;
}

.photo-grid {
  display: grid;
  gap: 22px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

@media (max-width: 720px) {
  .photo-grid {
    grid-template-columns: 1fr;
  }
}

.photo-card {
  border: 1px solid rgba(32, 45, 92, 0.14);
  border-radius: 20px;
  padding: 20px 22px;
  background: linear-gradient(180deg, rgba(244, 246, 255, 0.95) 0%, #ffffff 100%);
  display: flex;
  flex-direction: column;
  gap: 14px;
  box-shadow: inset 0 0 0 1px rgba(42, 61, 133, 0.05);
}

.photo-card__title {
  margin: 0;
  font-size: 1.02rem;
  font-weight: 600;
  color: #101938;
}

.photo-card__description {
  margin: 0;
  font-size: 0.92rem;
  line-height: 1.45;
  color: #1b2548;
}

.photo-card__upload {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.photo-card__upload-label {
  font-size: 0.9rem;
  font-weight: 600;
  color: #101938;
}

.photo-card__upload input[type='file'] {
  border: 1px dashed rgba(47, 68, 148, 0.35);
  border-radius: 14px;
  padding: 12px;
  background: rgba(255, 255, 255, 0.9);
  color: #101938;
}

.photo-card .file-meta {
  margin-top: 4px;
  color: #101938;
}

.photo-card__status {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.upload-progress {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 0.9rem;
  color: #2b3a69;
}

.spinner {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 2px solid rgba(43, 58, 105, 0.2);
  border-top-color: #4056d6;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.file-preview {
  width: 100%;
  border-radius: 12px;
  overflow: hidden;
  background: rgba(64, 86, 214, 0.08);
}

.file-preview img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.file-preview--pdf iframe {
  width: 100%;
  height: 160px;
  border: none;
  background: #ffffff;
}

.idoneita-grid {
  display: grid;
  gap: 18px;
}

@media (min-width: 860px) {
  .idoneita-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

.autocert-card {
  border: 1px solid rgba(76, 108, 255, 0.22);
  border-radius: 18px;
  padding: 18px 20px;
  background: rgba(248, 250, 255, 0.9);
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.autocert-card h3 {
  margin: 0;
  font-size: 1.05rem;
  color: #16285d;
  font-weight: 600;
}

.autocert-textarea {
  min-height: 160px;
  resize: vertical;
}

.legacy-autocert {
  display: flex;
  flex-direction: column;
  gap: 10px;
  align-items: flex-start;
}

.small-text {
  font-size: 0.9rem;
  line-height: 1.5;
}

.certification-panel {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 16px;
  background: rgba(76, 108, 255, 0.06);
}

.repeater-item .certification-panel {
  grid-column: 1 / -1;
}

.certification-upload {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.certification-upload input[type='file'] {
  border: none;
  padding: 0;
}

.certification-file-meta {
  justify-content: flex-start;
  gap: 12px;
}

.accordion-question {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 16px 18px;
  border-radius: 16px;
  background: rgba(76, 108, 255, 0.08);
}

.accordion-question span {
  font-weight: 600;
  color: #16285d;
}

.option-toggle {
  display: inline-flex;
  gap: 12px;
}

.choice-radio {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #ffffff;
  border: 1px solid rgba(76, 108, 255, 0.3);
  border-radius: 999px;
  padding: 6px 16px;
  cursor: pointer;
  transition: border-color 0.2s ease, background 0.2s ease, color 0.2s ease;
  color: #16285d;
}

.choice-radio input[type='radio'] {
  position: absolute;
  opacity: 0;
  pointer-events: none;
}

.choice-radio span {
  font-weight: 600;
  color: #16285d;
}

.choice-radio--checked {
  background: rgba(76, 108, 255, 0.16);
  color: #16285d;
  border-color: rgba(76, 108, 255, 0.45);
}

.choice-radio--checked span {
  color: #16285d;
}

.accordion-body {
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-top: 18px;
}

.file-meta {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
  font-size: 0.92rem;
}

.file-meta a {
  color: #3452e5;
  font-weight: 600;
  text-decoration: none;
}

.form-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
  padding-top: 22px;
  border-top: 1px solid rgba(76, 108, 255, 0.18);
}

.primary-button,
.secondary-button {
  border-radius: 999px;
  padding: 12px 28px;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.18s ease, box-shadow 0.25s ease, background 0.25s ease;
}

.primary-button {
  background: linear-gradient(135deg, #4663ff, #354de3);
  border: none;
  color: #ffffff;
}

.primary-button--success {
  background: linear-gradient(135deg, #22c55e, #16a34a);
  box-shadow: 0 16px 32px -22px rgba(34, 197, 94, 0.6);
}

.primary-button--success:hover:not(:disabled) {
  box-shadow: 0 18px 36px -18px rgba(22, 163, 74, 0.65);
}

.primary-button:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 16px 32px -22px rgba(69, 94, 255, 0.65);
}

.secondary-button {
  background: rgba(76, 108, 255, 0.08);
  border: 1px solid rgba(76, 108, 255, 0.36);
  color: #3452e5;
}

.secondary-button:hover:not(:disabled) {
  border-color: rgba(76, 108, 255, 0.6);
  color: #2537a8;
}

.primary-button:disabled,
.secondary-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.dirty-indicator {
  color: #f59e0b;
  font-weight: 600;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.25s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.fold-enter-active,
.fold-leave-active {
  transition: opacity 0.25s ease, transform 0.25s ease;
}

.fold-enter-from,
.fold-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

.toast-stack {
  position: fixed;
  top: 24px;
  right: 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  z-index: 1400;
}

.toast {
  min-width: 240px;
  max-width: 320px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 16px;
  border-radius: 12px;
  color: #f8fafc;
  box-shadow: 0 18px 32px rgba(15, 23, 42, 0.24);
  background: #1e293b;
}

.toast--success {
  background: #15803d;
}

.toast--error {
  background: #b91c1c;
}

.toast--info {
  background: #2563eb;
}

.toast-message {
  flex: 1;
  font-size: 0.9rem;
}

.toast-close {
  border: none;
  background: transparent;
  color: inherit;
  font-size: 18px;
  cursor: pointer;
  padding: 0;
  line-height: 1;
}

.toast-enter-active,
.toast-leave-active {
  transition: all 0.2s ease;
}

.toast-enter-from,
.toast-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

@media (max-width: 640px) {
  .toast-stack {
    left: 16px;
    right: 16px;
    top: 16px;
  }
  .toast {
    max-width: none;
    width: 100%;
  }
}

@media (max-width: 1280px) {
  .content-container {
    padding-right: 32px;
    padding-bottom: 56px;
  }
}

@media (max-width: 1024px) {
  .content-container {
    padding-right: 24px;
    padding-bottom: 48px;
  }

  .questionario-content {
    width: 100%;
  }

  .questionario-card {
    padding: 32px clamp(22px, 6vw, 34px);
    border-radius: 24px;
  }

  .qi-block {
    padding: 24px clamp(18px, 4vw, 28px);
  }
}

@media (max-width: 640px) {
  .content-container {
    padding-right: 18px;
    padding-bottom: 40px;
  }

  .questionario-card {
    padding: 28px 22px;
  }

  .card-header {
    align-items: flex-start;
  }

  .meta {
    width: 100%;
    justify-content: flex-start;
  }

  .qi-block__header {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
